import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  PromptTemplate,
  PromptTemplateDetail,
  PromptTemplateVersion,
  PromptVersionSummary,
  TestCase,
  TestRunSummary,
  TestRunResult,
  TestRunDetail,
  ServiceCriteria,
  MetricDefinition,
  DrinkPromotionConfig,
  DeepgramKeyword,
  TenantRole,
  GroundTruthComparison,
  GroundTruthMatchGroup,
  GroundTruthComparisonItem,
  GroundTruthMatchGroupItem,
} from '../../types/prompt';
import * as promptApi from '../../services/promptManagementService';
import { parseEmojiString } from '../../utils/metricUtils';

// ============================================================
// Sub-components
// ============================================================

const RoleSelector: React.FC<{
  roles: string[];
  tenantRoles: TenantRole[];
  selected: string;
  onSelect: (r: string) => void;
  onCreate: () => void;
}> = ({ roles, tenantRoles, selected, onSelect, onCreate }) => {
  const getDisplayName = (roleKey: string) => {
    const found = tenantRoles.find((tr) => tr.role_key === roleKey);
    return found ? found.display_name : roleKey;
  };

  return (
    <div className="flex items-center gap-2 mb-4 flex-wrap">
      {roles.map((r) => (
        <button
          key={r}
          onClick={() => onSelect(r)}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            selected === r
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          {getDisplayName(r)}
        </button>
      ))}
      <button
        onClick={onCreate}
        className="px-4 py-2 rounded-lg text-sm font-medium bg-green-50 text-green-700 hover:bg-green-100 border border-green-200"
      >
        + New Template
      </button>
    </div>
  );
};

const TestStatusBadge: React.FC<{ status?: PromptVersionSummary['test_status'] }> = ({ status }) => {
  if (!status || status.state === 'never_run') {
    return <span className="text-gray-400 text-xs">no test run</span>;
  }
  const counts = `${status.pass ?? 0}/${status.total ?? 0}`;
  if (status.state === 'passing') {
    return (
      <span
        className="px-2 py-0.5 rounded bg-green-100 text-green-800 text-xs font-medium"
        title={status.last_run ? `last run ${new Date(status.last_run).toLocaleString()}` : ''}
      >
        ✓ {counts} passing
      </span>
    );
  }
  if (status.state === 'warning') {
    return (
      <span
        className="px-2 py-0.5 rounded bg-yellow-100 text-yellow-800 text-xs font-medium"
        title={status.last_run ? `last run ${new Date(status.last_run).toLocaleString()}` : ''}
      >
        ~ {status.warn ?? 0} warn / {counts}
      </span>
    );
  }
  // failing
  return (
    <span
      className="px-2 py-0.5 rounded bg-red-100 text-red-800 text-xs font-medium"
      title={status.last_run ? `last run ${new Date(status.last_run).toLocaleString()}` : ''}
    >
      ✗ {(status.fail ?? 0) + (status.error ?? 0)} fail / {counts}
    </span>
  );
};

const VersionList: React.FC<{
  versions: PromptVersionSummary[];
  activeVersionId: string | null;
  onView: (id: string) => void;
  onActivate: (id: string) => void;
  onPreview: (id: string) => void;
}> = ({ versions, activeVersionId, onView, onActivate, onPreview }) => {
  const [showInactive, setShowInactive] = useState(false);

  const activeVersions = versions.filter((v) => v.is_active);
  const inactiveVersions = versions.filter((v) => !v.is_active);

  // Cutoff = earliest `created` among active versions. Inactive versions
  // created at-or-after this date are shown by default (they post-date the
  // currently-active config and are likely candidates for replacement).
  // Older inactive versions stay hidden behind the toggle.
  const activeCutoffMs = activeVersions
    .map((v) => (v.created ? new Date(v.created).getTime() : NaN))
    .filter((t) => !Number.isNaN(t))
    .reduce<number | null>((min, t) => (min === null || t < min ? t : min), null);

  const isNewerThanActive = (v: PromptVersionSummary) => {
    if (activeCutoffMs === null) return false;
    if (!v.created) return false;
    return new Date(v.created).getTime() >= activeCutoffMs;
  };

  const newerInactiveVersions = inactiveVersions.filter(isNewerThanActive);
  const olderInactiveVersions = inactiveVersions.filter((v) => !isNewerThanActive(v));

  const renderRow = (v: PromptVersionSummary) => {
    return (
      <tr key={v.id} className="border-b hover:bg-gray-50">
        <td className="py-2 pr-4 font-mono">v{v.version_number}</td>
        <td className="py-2 pr-4">
          {v.is_active ? (
            <span className="px-2 py-0.5 rounded bg-green-100 text-green-800 text-xs font-medium">
              Active
            </span>
          ) : (
            <span className="text-gray-400 text-xs">Inactive</span>
          )}
        </td>
        <td className="py-2 pr-4">
          <TestStatusBadge status={v.test_status} />
        </td>
        <td className="py-2 pr-4 text-gray-600">
          {v.created ? new Date(v.created).toLocaleDateString() : '-'}
        </td>
        <td className="py-2 pr-4 text-gray-600" title={v.created_by || ''}>
          {v.created_by_name || v.created_by || '-'}
        </td>
        <td className="py-2 pr-4 text-gray-600 max-w-xs truncate">
          {v.change_notes || '-'}
        </td>
        <td className="py-2 flex gap-2 flex-wrap">
          <button
            onClick={() => onView(v.id)}
            className="text-blue-600 hover:text-blue-800 text-xs"
          >
            View/Edit
          </button>
          <button
            onClick={() => onPreview(v.id)}
            className="text-purple-600 hover:text-purple-800 text-xs"
          >
            View Prompt
          </button>
          {!v.is_active && (
            <button
              onClick={() => onActivate(v.id)}
              className="text-green-600 hover:text-green-800 text-xs"
            >
              Activate
            </button>
          )}
        </td>
      </tr>
    );
  };

  return (
    <div className="mb-6">
      <h3 className="text-lg font-semibold mb-2">Versions</h3>
      {versions.length === 0 ? (
        <p className="text-gray-500 text-sm">No versions yet. Create one to get started.</p>
      ) : (
        <>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-gray-500">
                <th className="py-2 pr-4">#</th>
                <th className="py-2 pr-4">Status</th>
                <th className="py-2 pr-4">Tests</th>
                <th className="py-2 pr-4">Created</th>
                <th className="py-2 pr-4">By</th>
                <th className="py-2 pr-4">Notes</th>
                <th className="py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {activeVersions.map(renderRow)}
              {newerInactiveVersions.map(renderRow)}
              {showInactive && olderInactiveVersions.map(renderRow)}
            </tbody>
          </table>
          {olderInactiveVersions.length > 0 && (
            <button
              onClick={() => setShowInactive(!showInactive)}
              className="mt-2 text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1"
            >
              <span className={`inline-block transition-transform ${showInactive ? 'rotate-90' : ''}`}>&#9654;</span>
              {showInactive
                ? 'Hide older inactive versions'
                : `Show ${olderInactiveVersions.length} older inactive version${olderInactiveVersions.length !== 1 ? 's' : ''}`}
            </button>
          )}
        </>
      )}
    </div>
  );
};

// Emoji picker popover
const EMOJI_OPTIONS: Array<{ emoji: string; name: string }> = [
  // Food - meat & protein
  { emoji: '\u{1F969}', name: 'Steak' },
  { emoji: '\u{1F356}', name: 'Meat on Bone' },
  { emoji: '\u{1F357}', name: 'Poultry Leg' },
  { emoji: '\u{1F953}', name: 'Bacon' },
  { emoji: '\u{1F354}', name: 'Hamburger' },
  { emoji: '\u{1F32D}', name: 'Hot Dog' },
  { emoji: '\u{1F32E}', name: 'Taco' },
  { emoji: '\u{1F32F}', name: 'Burrito' },
  { emoji: '\u{1F9C6}', name: 'Falafel' },
  // Food - sweets & desserts
  { emoji: '\u{1F36B}', name: 'Chocolate Bar' },
  { emoji: '\u{1F370}', name: 'Cake Slice' },
  { emoji: '\u{1F382}', name: 'Birthday Cake' },
  { emoji: '\u{1F36A}', name: 'Cookie' },
  { emoji: '\u{1F369}', name: 'Doughnut' },
  { emoji: '\u{1F36E}', name: 'Custard' },
  { emoji: '\u{1F366}', name: 'Soft Ice Cream' },
  { emoji: '\u{1F367}', name: 'Shaved Ice' },
  { emoji: '\u{1F368}', name: 'Ice Cream' },
  // Food - meals & dishes
  { emoji: '\u{1F35D}', name: 'Spaghetti' },
  { emoji: '\u{1F35C}', name: 'Ramen' },
  { emoji: '\u{1F372}', name: 'Stew' },
  { emoji: '\u{1F35B}', name: 'Curry Rice' },
  { emoji: '\u{1F371}', name: 'Bento Box' },
  { emoji: '\u{1F363}', name: 'Sushi' },
  { emoji: '\u{1F364}', name: 'Fried Shrimp' },
  { emoji: '\u{1F35E}', name: 'Bread' },
  { emoji: '\u{1F950}', name: 'Croissant' },
  // Food - fruits & vegetables
  { emoji: '\u{1F34E}', name: 'Apple' },
  { emoji: '\u{1F34A}', name: 'Orange' },
  { emoji: '\u{1F34B}', name: 'Lemon' },
  { emoji: '\u{1F353}', name: 'Strawberry' },
  { emoji: '\u{1F347}', name: 'Grapes' },
  { emoji: '\u{1F349}', name: 'Watermelon' },
  { emoji: '\u{1F34C}', name: 'Banana' },
  { emoji: '\u{1F951}', name: 'Avocado' },
  { emoji: '\u{1F955}', name: 'Carrot' },
  // Drinks
  { emoji: '\u{1F379}', name: 'Tropical Drink' },
  { emoji: '\u{1F378}', name: 'Cocktail Glass' },
  { emoji: '\u{1F377}', name: 'Wine Glass' },
  { emoji: '\u{1F37A}', name: 'Beer Mug' },
  { emoji: '\u{1F37B}', name: 'Clinking Beers' },
  { emoji: '\u{2615}', name: 'Coffee' },
  { emoji: '\u{1F9CB}', name: 'Bubble Tea' },
  { emoji: '\u{1F964}', name: 'Cup with Straw' },
  { emoji: '\u{1F376}', name: 'Sake' },
  { emoji: '\u{1F9C3}', name: 'Beverage Box' },
  // Restaurant & service
  { emoji: '\u{2B50}', name: 'Star' },
  { emoji: '\u{1F31F}', name: 'Glowing Star' },
  { emoji: '\u{1F4AF}', name: '100' },
  { emoji: '\u{1F3C6}', name: 'Trophy' },
  { emoji: '\u{1F451}', name: 'Crown' },
  { emoji: '\u{1F44D}', name: 'Thumbs Up' },
  { emoji: '\u{1F44E}', name: 'Thumbs Down' },
  { emoji: '\u{1F4B0}', name: 'Money Bag' },
  { emoji: '\u{1F4B5}', name: 'Dollar' },
  // Other useful
  { emoji: '\u{1F525}', name: 'Fire' },
  { emoji: '\u{2764}\u{FE0F}', name: 'Heart' },
  { emoji: '\u{1F389}', name: 'Party Popper' },
  { emoji: '\u{1F388}', name: 'Balloon' },
  { emoji: '\u{1F381}', name: 'Gift' },
  { emoji: '\u{1F4A1}', name: 'Light Bulb' },
  { emoji: '\u{26A0}\u{FE0F}', name: 'Warning' },
  { emoji: '\u{274C}', name: 'Cross Mark' },
  { emoji: '\u{2705}', name: 'Check Mark' },
];

const EmojiPickerButton: React.FC<{
  value: string | undefined;
  onChange: (emoji: string) => void;
}> = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const display = value ? parseEmojiString(value) : null;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="border rounded px-2 py-1 text-sm w-16 h-8 flex items-center justify-center hover:bg-gray-50"
        title="Choose emoji"
      >
        {display ? <span className="text-xl leading-none">{display}</span> : <span className="text-gray-400">Emoji</span>}
      </button>
      {open && (
        <div className="absolute z-50 mt-1 bg-white border rounded-lg shadow-lg p-2 w-64">
          <div className="grid grid-cols-9 gap-1 max-h-48 overflow-y-auto">
            {EMOJI_OPTIONS.map(({ emoji: em, name }) => (
              <button
                key={em}
                type="button"
                title={name}
                onClick={() => { onChange(em); setOpen(false); }}
                className={`text-xl w-7 h-7 flex items-center justify-center rounded hover:bg-blue-100 ${value && parseEmojiString(value) === em ? 'bg-blue-200' : ''}`}
              >
                {em}
              </button>
            ))}
          </div>
          {value && (
            <button
              type="button"
              onClick={() => { onChange(''); setOpen(false); }}
              className="mt-1 text-xs text-red-500 hover:text-red-700 w-full text-center"
            >
              Clear
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// feat-088 (ADR D-8, raised 3→4 by ADR 0002 U1, 4→7 by feat-296): Server Scorecard cap. At most
// this many metrics may be flagged `show_on_scorecard` per tenant. The UI
// enforces it for UX; the backend is the authoritative gate (rejects an
// over-cap metric with a 400 on PUT and POST). Drink promotion is a SEPARATE
// opt-in column and does NOT count against this cap (ADR 0002 U2).
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const SCORECARD_CAP = 7;

// Small green-on toggle ("Show on Server Scorecard"). Tailwind-only switch so it
// stays native to the admin palette without the mockup's custom .switch CSS.
const ScorecardToggle: React.FC<{
  checked: boolean;
  disabled?: boolean;
  onChange: (v: boolean) => void;
  title?: string;
}> = ({ checked, disabled, onChange, title }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label="Show on Server Scorecard"
    disabled={disabled}
    onClick={() => onChange(!checked)}
    title={title}
    className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors ${
      checked ? 'bg-green-600' : 'bg-gray-300'
    } ${disabled ? 'opacity-45 cursor-not-allowed' : 'cursor-pointer'}`}
  >
    <span
      className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
        checked ? 'translate-x-4' : 'translate-x-0.5'
      }`}
    />
  </button>
);

// Re-number `display_order` to a clean 1..N over the flagged metrics (in their
// current order), and clear it on un-flagged metrics. Called after every toggle
// or move so the persisted config has no gaps/dupes — the backend reads
// display_order to order the scorecard columns (ADR D-8).
function normalizeScorecardOrder(metrics: MetricDefinition[]): MetricDefinition[] {
  const flaggedByRank = metrics
    .map((m, i) => ({ m, i }))
    .filter((x) => x.m.show_on_scorecard)
    .sort((a, b) => {
      const ao = a.m.display_order ?? Number.MAX_SAFE_INTEGER;
      const bo = b.m.display_order ?? Number.MAX_SAFE_INTEGER;
      return ao !== bo ? ao - bo : a.i - b.i; // stable by current position on ties
    });
  const rankByIndex = new Map<number, number>();
  flaggedByRank.forEach((x, rank) => rankByIndex.set(x.i, rank + 1));
  return metrics.map((m, i) => {
    if (m.show_on_scorecard) return { ...m, display_order: rankByIndex.get(i) };
    if (m.display_order === undefined) return m;
    const { display_order, ...rest } = m;
    return rest;
  });
}

// Metric row editor — collapsible. Collapsed: emoji · key · label · XP ·
// scorecard cluster (order + toggle) · ✕. Expanded: all fields in full width.
// New empty metrics auto-expand so the user can start typing immediately.
const MetricRow: React.FC<{
  metric: MetricDefinition;
  index: number;
  /** How many metrics are currently flagged for the scorecard (drives the cap). */
  flaggedCount: number;
  onChange: (index: number, m: MetricDefinition) => void;
  onRemove: (index: number) => void;
  onToggleScorecard: (index: number, next: boolean) => void;
  onMoveScorecard: (index: number, delta: -1 | 1) => void;
}> = ({ metric, index, flaggedCount, onChange, onRemove, onToggleScorecard, onMoveScorecard }) => {
  const isEmpty = !metric.key && !metric.label;
  const [expanded, setExpanded] = useState(isEmpty);

  const flagged = !!metric.show_on_scorecard;
  // Turning a metric ON past SCORECARD_CAP is blocked (UI mirror of the backend 400).
  const toggleDisabled = !flagged && flaggedCount >= SCORECARD_CAP;
  // Order is only meaningful when shown; display_order is normalized to 1..N.
  const order = flagged ? metric.display_order ?? null : null;
  const isFirst = order === 1;
  const isLast = order === flaggedCount;

  return (
    <div className="mb-2 bg-gray-50 rounded border border-gray-200 overflow-hidden">
      {/* Collapsed/header row — always visible. Clickable to toggle. */}
      <div className="flex items-stretch">
        <button
          type="button"
          onClick={() => setExpanded((e) => !e)}
          className="flex-1 flex items-center gap-3 px-3 py-2 hover:bg-gray-100 text-left min-w-0"
          aria-expanded={expanded}
          aria-controls={`metric-${index}-body`}
        >
          <span
            className={`text-xs text-gray-500 transition-transform inline-block w-3 ${
              expanded ? 'rotate-90' : ''
            }`}
            aria-hidden
          >
            ▶
          </span>
          <span className="text-lg leading-none w-5 text-center" aria-hidden>
            {metric.emoji ? parseEmojiString(metric.emoji) : '🔹'}
          </span>
          <span className="font-mono text-xs text-gray-600 truncate max-w-[220px]">
            {metric.key || <span className="italic text-gray-400">(no key)</span>}
          </span>
          <span className="text-sm text-gray-900 truncate flex-1 min-w-0">
            {metric.label || <span className="italic text-gray-400">(no label)</span>}
          </span>
          <span className="text-xs text-gray-500 tabular-nums shrink-0">
            {metric.xp_value ?? 15} XP
          </span>
        </button>
        {/* Scorecard control cluster — order stepper + show-on-scorecard toggle. */}
        <div className="flex items-center gap-2 px-3 border-l border-gray-200 bg-white">
          <div
            className={`flex flex-col items-center leading-none ${
              flagged ? 'text-gray-400' : 'text-gray-300 select-none'
            }`}
            title={flagged ? 'Column order on the Server Scorecard' : 'Set order when shown'}
          >
            <button
              type="button"
              disabled={!flagged || isFirst}
              onClick={() => onMoveScorecard(index, -1)}
              className="text-[10px] hover:text-gray-700 disabled:opacity-40 disabled:cursor-not-allowed"
              aria-label="Move earlier"
            >
              ▲
            </button>
            <span className="text-[11px] font-semibold tabular-nums text-gray-700">
              {order ?? '–'}
            </span>
            <button
              type="button"
              disabled={!flagged || isLast}
              onClick={() => onMoveScorecard(index, 1)}
              className="text-[10px] hover:text-gray-700 disabled:opacity-40 disabled:cursor-not-allowed"
              aria-label="Move later"
            >
              ▼
            </button>
          </div>
          <label className="flex items-center gap-1.5 cursor-pointer">
            <span className="text-[11px] text-gray-500 hidden sm:inline">Scorecard</span>
            <ScorecardToggle
              checked={flagged}
              disabled={toggleDisabled}
              onChange={(next) => onToggleScorecard(index, next)}
              title={
                toggleDisabled
                  ? `At most ${SCORECARD_CAP} metrics may be shown on the Server Scorecard`
                  : 'Show on Server Scorecard'
              }
            />
          </label>
        </div>
        <button
          type="button"
          onClick={() => onRemove(index)}
          className="px-3 text-red-500 hover:text-red-700 hover:bg-red-50 text-sm border-l border-gray-200"
          aria-label={`Remove metric ${metric.key || index + 1}`}
          title="Remove this metric"
        >
          ✕
        </button>
      </div>

      {/* Expanded body — full-width labeled fields. */}
      {expanded && (
        <div
          id={`metric-${index}-body`}
          className="px-3 pb-3 pt-2 space-y-3 bg-white border-t border-gray-200"
        >
          {/* Top: key, label, emoji, xp */}
          <div className="grid grid-cols-12 gap-3">
            <div className="col-span-4">
              <label className="block text-xs text-gray-500 mb-1">Key</label>
              <input
                className="border rounded px-2 py-1 text-sm w-full font-mono"
                placeholder="e.g. greeter_upsell"
                value={metric.key}
                onChange={(e) => onChange(index, { ...metric, key: e.target.value })}
              />
            </div>
            <div className="col-span-5">
              <label className="block text-xs text-gray-500 mb-1">Label</label>
              <input
                className="border rounded px-2 py-1 text-sm w-full"
                placeholder="Human-readable name"
                value={metric.label}
                onChange={(e) => onChange(index, { ...metric, label: e.target.value })}
              />
            </div>
            <div className="col-span-1">
              <label className="block text-xs text-gray-500 mb-1">Emoji</label>
              <EmojiPickerButton
                value={metric.emoji}
                onChange={(em) => onChange(index, { ...metric, emoji: em })}
              />
            </div>
            <div className="col-span-2">
              <label
                className="block text-xs text-gray-500 mb-1"
                title="Base XP awarded when this metric is fulfilled. L2/L3 quality adds the bonuses configured below."
              >
                XP
              </label>
              <input
                className="border rounded px-2 py-1 text-sm w-full text-center"
                type="number"
                min={0}
                value={metric.xp_value ?? 15}
                onChange={(e) =>
                  onChange(index, {
                    ...metric,
                    xp_value: parseInt(e.target.value) || 0,
                  })
                }
              />
            </div>
          </div>

          {/* Description — full width, multi-line */}
          <div>
            <label className="block text-xs text-gray-500 mb-1">Description</label>
            <textarea
              className="border rounded px-2 py-1 text-sm w-full"
              rows={2}
              placeholder="What this metric checks. Shown to Claude as part of the Service Criteria section."
              value={metric.description || ''}
              onChange={(e) =>
                onChange(index, { ...metric, description: e.target.value })
              }
            />
          </div>

          {/* Applies to — full width */}
          <div>
            <label className="block text-xs text-gray-500 mb-1">
              Applies to interaction types{' '}
              <span className="text-gray-400">(comma-separated)</span>
            </label>
            <input
              className="border rounded px-2 py-1 text-sm w-full"
              placeholder="e.g. cashier_upsell, greeter_upsell, garnish_complete"
              value={(metric.applies_to || []).join(', ')}
              onChange={(e) => {
                const applies_to = e.target.value
                  .split(',')
                  .map((s) => s.trim())
                  .filter(Boolean);
                onChange(index, { ...metric, applies_to });
              }}
            />
          </div>

          {/* Examples — full width, more rows */}
          <div>
            <label className="block text-xs text-gray-500 mb-1">
              Examples <span className="text-gray-400">(one per line)</span>
            </label>
            <textarea
              className="border rounded px-2 py-1 text-sm w-full font-mono"
              rows={4}
              placeholder={`L2: "Want to add a Tomato Bisque to your sandwich?"\nL3: "Our Tomato Bisque is the classic pairing — creamy and fresh today."`}
              value={(metric.examples || []).join('\n')}
              onChange={(e) => {
                const lines = e.target.value.split('\n');
                const examples =
                  lines.length === 1 && lines[0] === '' ? [] : lines;
                onChange(index, { ...metric, examples });
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================
// Version Editor
// ============================================================

// Exported for unit testing (feat-156 — the Automated Validations tab relocates
// Double Check + adds the Judge prompt; the round-trip is verified in isolation).
export const VersionEditor: React.FC<{
  version: PromptTemplateVersion | null;
  templateId: string;
  isActive: boolean;
  // feat-156 — which field group this instance renders. The SAME VersionEditor
  // instance is mounted for both the Versions tab ('core') and the new Automated
  // Validations tab ('validations'); React preserves its form state across the
  // tab switch (same key + tree position), so a single save via buildPayload
  // still includes every field regardless of which tab it was edited on.
  section?: 'core' | 'validations';
  onSaved: () => void;
  onCancel: () => void;
  onPreview: (id: string) => void;
}> = ({ version, templateId, isActive, section = 'core', onSaved, onCancel, onPreview }) => {
  const [context, setContext] = useState(version?.context || '');
  const [considerations, setConsiderations] = useState(version?.considerations || '');
  const [interactionTypes, setInteractionTypes] = useState(version?.interaction_types || '');
  const [analysisRequirements, setAnalysisRequirements] = useState(version?.analysis_requirements || '');
  const [outputFormatTemplate, setOutputFormatTemplate] = useState(version?.output_format_template || '');
  const [doubleCheckTemplate, setDoubleCheckTemplate] = useState(version?.double_check_template || '');
  // feat-156 — grade-aware offline-judge prompt, edited on the Automated
  // Validations tab alongside the relocated Double Check prompt.
  const [judgeTemplate, setJudgeTemplate] = useState(version?.judge_template || '');
  const [recommendationsTemplate, setRecommendationsTemplate] = useState(version?.recommendations_template || '');
  const [menu, setMenu] = useState(version?.menu || '');
  const [examples, setExamples] = useState(version?.examples || '');
  const [changeNotes, setChangeNotes] = useState(version?.change_notes || '');
  const [saving, setSaving] = useState(false);
  const [expandedField, setExpandedField] = useState<{ label: string; value: string; setter: (v: string) => void } | null>(null);

  // Service criteria
  const [metrics, setMetrics] = useState<MetricDefinition[]>(
    version?.service_criteria?.metrics || []
  );
  const [drinkPromo, setDrinkPromo] = useState<DrinkPromotionConfig>(
    version?.service_criteria?.drink_promotion || { enabled: false }
  );
  const [scoreMultiplier, setScoreMultiplier] = useState(
    version?.service_criteria?.score_multiplier ?? 2
  );
  const [deepgramKeywords, setDeepgramKeywords] = useState<DeepgramKeyword[]>(
    version?.service_criteria?.deepgram_keywords || []
  );
  const [showKeywords, setShowKeywords] = useState(false);
  const [xpConfig, setXpConfig] = useState({
    greeting_xp: version?.service_criteria?.xp_config?.greeting_xp ?? 10,
    drink_promotion_xp: version?.service_criteria?.xp_config?.drink_promotion_xp ?? 15,
    l2_bonus: version?.service_criteria?.xp_config?.l2_bonus ?? 2,
    l3_bonus: version?.service_criteria?.xp_config?.l3_bonus ?? 4,
  });

  const handleMetricChange = (index: number, m: MetricDefinition) => {
    const updated = [...metrics];
    updated[index] = m;
    setMetrics(updated);
  };

  // ── feat-088 (ADR D-8): scorecard flag + column order ──────────────────────
  const flaggedCount = metrics.filter((m) => m.show_on_scorecard).length;
  const atScorecardCap = flaggedCount >= SCORECARD_CAP;

  const handleToggleScorecard = (index: number, next: boolean) => {
    setMetrics((prev) => {
      // Cap is enforced on turn-ON. The toggle is also disabled in the UI; this
      // guard makes the handler safe even if that's bypassed. Backend 400 is the
      // authoritative gate.
      if (next && prev.filter((m) => m.show_on_scorecard).length >= SCORECARD_CAP) {
        return prev;
      }
      const updated = prev.map((m, i) =>
        i === index ? { ...m, show_on_scorecard: next } : m,
      );
      return normalizeScorecardOrder(updated);
    });
  };

  const handleMoveScorecard = (index: number, delta: -1 | 1) => {
    setMetrics((prev) => {
      const flaggedByRank = prev
        .map((m, i) => ({ m, i }))
        .filter((x) => x.m.show_on_scorecard)
        .sort((a, b) => (a.m.display_order ?? 0) - (b.m.display_order ?? 0));
      const pos = flaggedByRank.findIndex((x) => x.i === index);
      const swap = pos + delta;
      if (pos < 0 || swap < 0 || swap >= flaggedByRank.length) return prev;
      const a = flaggedByRank[pos];
      const b = flaggedByRank[swap];
      const updated = prev.map((m, i) => {
        if (i === a.i) return { ...m, display_order: b.m.display_order };
        if (i === b.i) return { ...m, display_order: a.m.display_order };
        return m;
      });
      return normalizeScorecardOrder(updated);
    });
  };

  const canUpdateInPlace = !!version && !isActive;
  // feat-319: a deep-linked version may belong to a different template than
  // the selected role's tenant-wide one (a location override). New versions
  // always go to the template the edited version came from.
  const saveTemplateId = version?.template_id || templateId;

  const buildPayload = () => {
    const serviceCriteria: ServiceCriteria = {
      metrics,
      drink_promotion: drinkPromo,
      score_multiplier: scoreMultiplier,
      deepgram_keywords: deepgramKeywords.length > 0 ? deepgramKeywords : undefined,
      xp_config: xpConfig,
    };
    return {
      context,
      considerations,
      interaction_types: interactionTypes,
      service_criteria: serviceCriteria,
      menu,
      examples,
      output_format_template: outputFormatTemplate || null,
      double_check_template: doubleCheckTemplate || null,
      judge_template: judgeTemplate || null,
      recommendations_template: recommendationsTemplate || null,
      analysis_requirements: analysisRequirements || null,
      change_notes: changeNotes || null,
    };
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (canUpdateInPlace) {
        await promptApi.updateVersion(version!.id, buildPayload());
      } else {
        await promptApi.createVersion(saveTemplateId, buildPayload());
      }
      onSaved();
    } catch (err) {
      alert('Failed to save version: ' + (err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAsNew = async () => {
    setSaving(true);
    try {
      await promptApi.createVersion(saveTemplateId, buildPayload());
      onSaved();
    } catch (err) {
      alert('Failed to save version: ' + (err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white border rounded-lg p-4 mb-6">
      <h3 className="text-lg font-semibold mb-4">
        {!version
          ? 'New Version'
          : canUpdateInPlace
            ? `Editing v${version.version_number}`
            : `Edit (based on v${version.version_number} — active, will save as new)`}
      </h3>

      {/* feat-156 — Core version fields (Versions tab). The Double Check + Judge
          prompts live in the 'validations' section (Automated Validations tab). */}
      {section === 'core' && (
      <>
      <div className="flex items-center gap-2 mb-1">
        <label className="text-sm font-medium text-gray-700">Context (AI persona)</label>
        <button onClick={() => setExpandedField({ label: 'Context (AI persona)', value: context, setter: setContext })} className="text-gray-400 hover:text-gray-600 text-sm" title="Edit fullscreen">⊞</button>
      </div>
      <textarea
        className="w-full border rounded p-2 text-sm mb-3 font-mono"
        rows={4}
        value={context}
        onChange={(e) => setContext(e.target.value)}
      />

      <div className="flex items-center gap-2 mb-1">
        <label className="text-sm font-medium text-gray-700">Considerations</label>
        <button onClick={() => setExpandedField({ label: 'Considerations', value: considerations, setter: setConsiderations })} className="text-gray-400 hover:text-gray-600 text-sm" title="Edit fullscreen">⊞</button>
      </div>
      <textarea
        className="w-full border rounded p-2 text-sm mb-3 font-mono"
        rows={6}
        value={considerations}
        onChange={(e) => setConsiderations(e.target.value)}
      />

      <div className="flex items-center gap-2 mb-1">
        <label className="text-sm font-medium text-gray-700">Interaction Types</label>
        <button onClick={() => setExpandedField({ label: 'Interaction Types', value: interactionTypes, setter: setInteractionTypes })} className="text-gray-400 hover:text-gray-600 text-sm" title="Edit fullscreen">⊞</button>
      </div>
      <textarea
        className="w-full border rounded p-2 text-sm mb-3 font-mono"
        rows={10}
        value={interactionTypes}
        onChange={(e) => setInteractionTypes(e.target.value)}
      />

      <div className="flex items-center gap-2 mb-1">
        <label className="text-sm font-medium text-gray-700">Analysis Requirements</label>
        <button onClick={() => setExpandedField({ label: 'Analysis Requirements', value: analysisRequirements, setter: setAnalysisRequirements })} className="text-gray-400 hover:text-gray-600 text-sm" title="Edit fullscreen">⊞</button>
      </div>
      <textarea
        className="w-full border rounded p-2 text-sm mb-3 font-mono"
        rows={8}
        value={analysisRequirements}
        onChange={(e) => setAnalysisRequirements(e.target.value)}
        placeholder="Leave empty to use default analysis requirements"
      />

      {/* Service Criteria */}
      <div className="mb-3">
        <div className="flex items-center justify-between mb-1">
          <label className="block text-sm font-medium text-gray-700">
            Service Criteria (Metrics)
          </label>
          {/* feat-088: running count of metrics flagged for the Server Scorecard. */}
          <span
            className={`text-xs font-medium rounded-full px-2 py-0.5 ${
              atScorecardCap
                ? 'text-amber-700 bg-amber-50 border border-amber-200'
                : 'text-gray-500 bg-gray-100'
            }`}
          >
            {flaggedCount} / {SCORECARD_CAP} on scorecard{atScorecardCap ? ' · max' : ''}
          </span>
        </div>
        <p className="text-xs text-gray-500 mb-2">
          Toggle <span className="font-semibold">Scorecard</span> to surface a metric's per-window
          total as a column on the Server Scorecard. Up to {SCORECARD_CAP}; the order stepper sets
          the column order.
        </p>
        {metrics.map((m, i) => (
          <MetricRow
            key={i}
            metric={m}
            index={i}
            flaggedCount={flaggedCount}
            onChange={handleMetricChange}
            onRemove={(idx) =>
              setMetrics(normalizeScorecardOrder(metrics.filter((_, j) => j !== idx)))
            }
            onToggleScorecard={handleToggleScorecard}
            onMoveScorecard={handleMoveScorecard}
          />
        ))}
        {atScorecardCap && (
          <div className="flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2 mb-2">
            <span aria-hidden>⚠️</span>
            <span>
              You can show up to <span className="font-semibold">{SCORECARD_CAP}</span> metrics on the
              Server Scorecard. Turn one off to add another.{' '}
              <span className="text-amber-600">
                (The backend also rejects {SCORECARD_CAP + 1} or more with a 400 — UI + server enforce
                the same cap.)
              </span>
            </span>
          </div>
        )}
        <button
          onClick={() =>
            setMetrics([
              ...metrics,
              { key: `metric_${metrics.length + 1}`, label: '', applies_to: ['greeter_upsell'] },
            ])
          }
          className="text-sm text-blue-600 hover:text-blue-800"
        >
          + Add Metric
        </button>
      </div>

      {/* Drink Promotion */}
      <div className="mb-3 p-3 bg-gray-50 rounded">
        <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
          <input
            type="checkbox"
            checked={drinkPromo.enabled}
            onChange={(e) => setDrinkPromo({ ...drinkPromo, enabled: e.target.checked })}
          />
          Drink Promotion
        </label>
        {drinkPromo.enabled && (
          <div className="space-y-2">
            <div className="flex gap-2">
              <input
                className="border rounded px-2 py-1 text-sm"
                placeholder="Drink name"
                value={drinkPromo.drink_name || ''}
                onChange={(e) => setDrinkPromo({ ...drinkPromo, drink_name: e.target.value })}
              />
              <input
                className="border rounded px-2 py-1 text-sm"
                placeholder="Label"
                value={drinkPromo.label || ''}
                onChange={(e) => setDrinkPromo({ ...drinkPromo, label: e.target.value })}
              />
              <EmojiPickerButton
                value={drinkPromo.emoji}
                onChange={(em) => setDrinkPromo({ ...drinkPromo, emoji: em })}
              />
            </div>
            {/* feat-088 (ADR 0002 U2): drink scorecard column — independent of the
                ≤SCORECARD_CAP metric cap (it does NOT consume a metric slot). */}
            <label className="flex items-center gap-2 cursor-pointer w-fit">
              <ScorecardToggle
                checked={!!drinkPromo.show_on_scorecard}
                onChange={(next) => setDrinkPromo({ ...drinkPromo, show_on_scorecard: next })}
                title="Show drink-promotion totals as a column on the Server Scorecard"
              />
              <span className="text-xs text-gray-600">Show on Server Scorecard</span>
              <span className="text-[11px] text-gray-400">
                (separate column — doesn&apos;t count toward the {SCORECARD_CAP}-metric limit)
              </span>
            </label>
          </div>
        )}
      </div>

      {/* Score multiplier */}
      <div className="mb-3">
        <label className="block text-sm font-medium text-gray-700 mb-1">Score Multiplier</label>
        <input
          type="number"
          className="border rounded px-2 py-1 text-sm w-20"
          value={scoreMultiplier}
          onChange={(e) => setScoreMultiplier(Number(e.target.value))}
        />
      </div>

      {/* XP Configuration */}
      <div className="mb-3 p-3 bg-blue-50 rounded">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          XP Configuration
        </label>
        <div className="grid grid-cols-4 gap-3">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Greeting XP</label>
            <input
              type="number" min={0}
              className="border rounded px-2 py-1 text-sm w-full"
              value={xpConfig.greeting_xp}
              onChange={(e) => setXpConfig({ ...xpConfig, greeting_xp: parseInt(e.target.value) || 0 })}
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Drink Promo XP</label>
            <input
              type="number" min={0}
              className="border rounded px-2 py-1 text-sm w-full"
              value={xpConfig.drink_promotion_xp}
              onChange={(e) => setXpConfig({ ...xpConfig, drink_promotion_xp: parseInt(e.target.value) || 0 })}
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">L2 Bonus (+XP)</label>
            <input
              type="number" min={0}
              className="border rounded px-2 py-1 text-sm w-full"
              value={xpConfig.l2_bonus}
              onChange={(e) => setXpConfig({ ...xpConfig, l2_bonus: parseInt(e.target.value) || 0 })}
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">L3 Bonus (+XP)</label>
            <input
              type="number" min={0}
              className="border rounded px-2 py-1 text-sm w-full"
              value={xpConfig.l3_bonus}
              onChange={(e) => setXpConfig({ ...xpConfig, l3_bonus: parseInt(e.target.value) || 0 })}
            />
          </div>
        </div>
        <p className="text-xs text-gray-400 mt-1">
          Per-metric XP values are set on each metric above. Quality bonuses: L2 adds +{xpConfig.l2_bonus} XP, L3 adds +{xpConfig.l3_bonus} XP to the base.
        </p>
      </div>

      {/* Deepgram Keywords */}
      <div className="mb-3">
        <button
          type="button"
          onClick={() => setShowKeywords(!showKeywords)}
          className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1"
        >
          <span className={`inline-block transition-transform text-xs ${showKeywords ? 'rotate-90' : ''}`}>&#9654;</span>
          Deepgram Keywords
          <span className="text-gray-400 font-normal">({deepgramKeywords.length})</span>
        </button>
        {!showKeywords && deepgramKeywords.length > 0 && (
          <div className="flex flex-wrap gap-1 ml-5">
            {deepgramKeywords.filter(kw => kw.keyword).map((kw, i) => (
              <span key={i} className="inline-flex items-center px-2 py-0.5 rounded-full bg-gray-100 text-xs text-gray-600">
                {kw.keyword}{kw.intensifier !== 1 && <span className="ml-1 text-gray-400">({kw.intensifier})</span>}
              </span>
            ))}
          </div>
        )}
        {showKeywords && (
          <div className="ml-5 p-3 bg-gray-50 rounded">
            <div className="max-h-64 overflow-y-auto">
              {deepgramKeywords.map((kw, i) => (
                <div key={i} className="flex items-center gap-2 mb-1">
                  <input
                    className="border rounded px-2 py-1 text-sm flex-1"
                    placeholder="Keyword"
                    value={kw.keyword}
                    onChange={(e) => {
                      const updated = [...deepgramKeywords];
                      updated[i] = { ...kw, keyword: e.target.value.replace(/\s/g, '') };
                      setDeepgramKeywords(updated);
                    }}
                  />
                  <input
                    type="number"
                    step="0.5"
                    className="border rounded px-2 py-1 text-sm w-24"
                    placeholder="Intensifier"
                    value={kw.intensifier}
                    onChange={(e) => {
                      const updated = [...deepgramKeywords];
                      updated[i] = { ...kw, intensifier: Number(e.target.value) };
                      setDeepgramKeywords(updated);
                    }}
                  />
                  <button
                    onClick={() => setDeepgramKeywords(deepgramKeywords.filter((_, j) => j !== i))}
                    className="text-red-400 hover:text-red-600 text-sm px-1"
                    title="Remove keyword"
                  >
                    X
                  </button>
                </div>
              ))}
            </div>
            <button
              onClick={() => setDeepgramKeywords([...deepgramKeywords, { keyword: '', intensifier: 1 }])}
              disabled={deepgramKeywords.length >= 100}
              className="text-sm text-blue-600 hover:text-blue-800 disabled:opacity-50 disabled:cursor-not-allowed mt-1"
            >
              + Add Keyword
            </button>
            <p className="text-xs text-gray-500 mt-1">
              Intensifier is an exponential boost factor (default 1, use negative to suppress).
            </p>
          </div>
        )}
      </div>

      <div className="flex items-center gap-2 mb-1">
        <label className="text-sm font-medium text-gray-700">Menu</label>
        <button onClick={() => setExpandedField({ label: 'Menu', value: menu, setter: setMenu })} className="text-gray-400 hover:text-gray-600 text-sm" title="Edit fullscreen">⊞</button>
      </div>
      <textarea
        className="w-full border rounded p-2 text-sm mb-3 font-mono"
        rows={8}
        value={menu}
        onChange={(e) => setMenu(e.target.value)}
      />

      <div className="flex items-center gap-2 mb-1">
        <label className="text-sm font-medium text-gray-700">Examples</label>
        <button onClick={() => setExpandedField({ label: 'Examples', value: examples, setter: setExamples })} className="text-gray-400 hover:text-gray-600 text-sm" title="Edit fullscreen">⊞</button>
      </div>
      <textarea
        className="w-full border rounded p-2 text-sm mb-3 font-mono"
        rows={6}
        value={examples}
        onChange={(e) => setExamples(e.target.value)}
      />

      <div className="flex items-center gap-2 mb-1">
        <label className="text-sm font-medium text-gray-700">Output Format</label>
        <button onClick={() => setExpandedField({ label: 'Output Format', value: outputFormatTemplate, setter: setOutputFormatTemplate })} className="text-gray-400 hover:text-gray-600 text-sm" title="Edit fullscreen">⊞</button>
      </div>
      <textarea
        className="w-full border rounded p-2 text-sm mb-3 font-mono"
        rows={6}
        value={outputFormatTemplate}
        onChange={(e) => setOutputFormatTemplate(e.target.value)}
      />

      <div className="flex items-center gap-2 mb-1">
        <label className="text-sm font-medium text-gray-700">Recommendations</label>
        <button onClick={() => setExpandedField({ label: 'Recommendations', value: recommendationsTemplate, setter: setRecommendationsTemplate })} className="text-gray-400 hover:text-gray-600 text-sm" title="Edit fullscreen">⊞</button>
      </div>
      <textarea
        className="w-full border rounded p-2 text-sm mb-3 font-mono"
        rows={6}
        value={recommendationsTemplate}
        onChange={(e) => setRecommendationsTemplate(e.target.value)}
      />
      </>
      )}

      {/* feat-156 — Automated Validations section (Automated Validations tab):
          the relocated Double Check prompt + the new grade-aware Judge prompt.
          Both persist as prompt_template_version columns via the SAME save path. */}
      {section === 'validations' && (
      <>
      <p className="text-sm text-gray-600 mb-4">
        These prompts drive the automated validation layer. <span className="font-medium">Double Check</span> is
        the live second-pass that confirms or discards each detected interaction during a shift.
        <span className="font-medium"> Judge Prompt</span> is the independent, grade-aware offline judge that
        re-scores stored greeting and upsell interactions. Both are versioned with this template — leave a
        field blank to use the built-in default.
      </p>

      <div className="flex items-center gap-2 mb-1">
        <label className="text-sm font-medium text-gray-700">Double Check</label>
        <button onClick={() => setExpandedField({ label: 'Double Check', value: doubleCheckTemplate, setter: setDoubleCheckTemplate })} className="text-gray-400 hover:text-gray-600 text-sm" title="Edit fullscreen">⊞</button>
      </div>
      <textarea
        className="w-full border rounded p-2 text-sm mb-3 font-mono"
        rows={6}
        value={doubleCheckTemplate}
        onChange={(e) => setDoubleCheckTemplate(e.target.value)}
      />

      <div className="flex items-center gap-2 mb-1">
        <label className="text-sm font-medium text-gray-700">Judge Prompt</label>
        <button onClick={() => setExpandedField({ label: 'Judge Prompt', value: judgeTemplate, setter: setJudgeTemplate })} className="text-gray-400 hover:text-gray-600 text-sm" title="Edit fullscreen">⊞</button>
      </div>
      <textarea
        className="w-full border rounded p-2 text-sm mb-3 font-mono"
        rows={6}
        value={judgeTemplate}
        onChange={(e) => setJudgeTemplate(e.target.value)}
        placeholder="Leave empty to use the built-in grade-aware judge prompt"
      />
      </>
      )}

      <label className="block text-sm font-medium text-gray-700 mb-1">Change Notes</label>
      <input
        className="w-full border rounded px-2 py-1 text-sm mb-4"
        placeholder="What changed in this version?"
        value={changeNotes}
        onChange={(e) => setChangeNotes(e.target.value)}
      />

      <div className="flex gap-2">
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 text-sm"
        >
          {saving ? 'Saving...' : canUpdateInPlace ? `Save (v${version!.version_number})` : 'Save as New Version'}
        </button>
        {canUpdateInPlace && (
          <button
            onClick={handleSaveAsNew}
            disabled={saving}
            className="px-4 py-2 bg-white text-blue-600 rounded hover:bg-blue-50 border border-blue-200 disabled:opacity-50 text-sm"
          >
            Save as New Version
          </button>
        )}
        <button
          onClick={onCancel}
          className="px-4 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 text-sm"
        >
          Cancel
        </button>
        {version && (
          <button
            onClick={() => onPreview(version.id)}
            className="px-4 py-2 bg-purple-50 text-purple-700 rounded hover:bg-purple-100 border border-purple-200 text-sm"
          >
            View Assembled Prompt
          </button>
        )}
      </div>

      {/* Fullscreen field editor */}
      {expandedField && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white fixed inset-4 rounded-lg shadow-xl flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold">{expandedField.label}</h3>
              <button
                onClick={() => setExpandedField(null)}
                className="text-gray-400 hover:text-gray-600 text-xl leading-none"
              >
                &times;
              </button>
            </div>
            <textarea
              className="flex-1 m-4 border rounded p-3 text-sm font-mono resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={expandedField.value}
              onChange={(e) => {
                const newValue = e.target.value;
                expandedField.setter(newValue);
                setExpandedField({ ...expandedField, value: newValue });
              }}
              autoFocus
            />
            <div className="p-4 border-t flex justify-end">
              <button
                onClick={() => setExpandedField(null)}
                className="px-4 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================
// Ground Truth Comparison Display
// ============================================================

const VerdictBadge: React.FC<{ verdict: string }> = ({ verdict }) => {
  const cls =
    verdict === 'pass'
      ? 'bg-green-100 text-green-800'
      : verdict === 'warn'
      ? 'bg-yellow-100 text-yellow-800'
      : 'bg-red-100 text-red-800';
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${cls}`}>
      {verdict.toUpperCase()}
    </span>
  );
};

const MatchGroupTable: React.FC<{
  group: GroundTruthMatchGroup;
  label: string;
  isDrink?: boolean;
}> = ({ group, label, isDrink }) => {
  if (group.matched.length === 0 && group.missing.length === 0 && group.extra.length === 0) {
    return null;
  }

  // Collect all metric keys that appear across any matched item (union of expected + actual)
  const metricKeys: string[] = [];
  if (!isDrink) {
    const keySet = new Set<string>();
    for (const m of group.matched) {
      for (const k of Object.keys(m.expected.service_criteria || {})) keySet.add(k);
      for (const k of Object.keys(m.actual.service_criteria || {})) keySet.add(k);
    }
    for (const item of group.missing) {
      for (const k of Object.keys(item.service_criteria || {})) keySet.add(k);
    }
    for (const item of group.extra) {
      for (const k of Object.keys(item.service_criteria || {})) keySet.add(k);
    }
    metricKeys.push(...Array.from(keySet).sort());
  }

  const renderCriteriaBadge = (fulfilled: boolean | undefined | null, changed?: boolean) => {
    if (fulfilled === undefined || fulfilled === null) {
      return <span className="text-gray-300">-</span>;
    }
    const base = fulfilled ? 'text-green-600' : 'text-red-500';
    return (
      <span className={`${base} ${changed ? 'font-bold underline' : ''}`}>
        {fulfilled ? 'Y' : 'N'}
      </span>
    );
  };

  const renderMissingExtra = (items: GroundTruthMatchGroupItem[], color: 'red' | 'yellow') => {
    if (items.length === 0) return null;
    const bg = color === 'red' ? 'bg-red-50 text-red-700' : 'bg-yellow-50 text-yellow-700';
    const labelCls = color === 'red' ? 'text-red-600' : 'text-yellow-600';
    const word = color === 'red' ? 'Missing' : 'Extra';
    return (
      <div className="mb-1">
        <span className={`text-xs ${labelCls} font-medium`}>{word} ({items.length}):</span>
        <div className="flex flex-wrap gap-1 mt-0.5">
          {items.map((m, i) => (
            <span key={i} className={`px-2 py-0.5 ${bg} rounded text-xs`}>
              {m.type || m.drink_promoted || `@${m.start_timestamp}`}
              {!isDrink && m.service_criteria && (
                <span className="ml-1 text-gray-500">
                  [{Object.entries(m.service_criteria).map(([k, v]) => `${k}:${v ? 'Y' : 'N'}`).join(' ')}]
                </span>
              )}
            </span>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="mb-3">
      <h6 className="text-xs font-medium text-gray-600 mb-1">{label}</h6>
      {group.matched.length > 0 && (
        <div className="overflow-x-auto mb-1">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b text-left text-gray-500">
                {!isDrink && <th className="py-1 pr-2">Type</th>}
                <th className="py-1 pr-2">Start</th>
                <th className="py-1 pr-2">End</th>
                {isDrink && <th className="py-1 pr-2">Drink</th>}
                {metricKeys.map((k) => (
                  <th key={k} className="py-1 pr-2 text-center">{k}</th>
                ))}
                <th className="py-1">Status</th>
              </tr>
            </thead>
            <tbody>
              {group.matched.map((m: GroundTruthComparisonItem, i: number) => {
                // Only count diffs that are significant (not within_tolerance) for status
                const hasSignificantDiffs = Object.entries(m.diffs).some(([, v]) => {
                  if (v && typeof v === 'object' && 'within_tolerance' in v && (v as any).within_tolerance) return false;
                  return true;
                });
                const criteriaDiffs = m.diffs.service_criteria || {};
                return (
                  <tr key={i} className={`border-b ${hasSignificantDiffs ? 'bg-yellow-50' : ''}`}>
                    {!isDrink && <td className="py-1 pr-2">{m.type || '-'}</td>}
                    <td className="py-1 pr-2">
                      {m.diffs.start_timestamp ? (
                        <span className={m.diffs.start_timestamp.within_tolerance ? 'text-gray-400' : 'text-yellow-700'}>
                          {String(m.diffs.start_timestamp.expected)} &rarr; {String(m.diffs.start_timestamp.actual)}
                        </span>
                      ) : (
                        String(m.actual.start_timestamp ?? '-')
                      )}
                    </td>
                    <td className="py-1 pr-2">
                      {m.diffs.end_timestamp ? (
                        <span className={m.diffs.end_timestamp.within_tolerance ? 'text-gray-400' : 'text-yellow-700'}>
                          {String(m.diffs.end_timestamp.expected)} &rarr; {String(m.diffs.end_timestamp.actual)}
                        </span>
                      ) : (
                        String(m.actual.end_timestamp ?? '-')
                      )}
                    </td>
                    {isDrink && (
                      <td className="py-1 pr-2">
                        {m.diffs.drink_promoted ? (
                          <span className="text-yellow-700">
                            {String(m.diffs.drink_promoted.expected)} &rarr; {String(m.diffs.drink_promoted.actual)}
                          </span>
                        ) : (
                          String(m.actual.drink_promoted ?? '-')
                        )}
                      </td>
                    )}
                    {metricKeys.map((k) => {
                      const diff = criteriaDiffs[k];
                      if (diff) {
                        return (
                          <td key={k} className="py-1 pr-2 text-center bg-yellow-100">
                            <span className="text-yellow-700">
                              {diff.expected != null ? (diff.expected ? 'Y' : 'N') : '-'}
                              {' \u2192 '}
                              {diff.actual != null ? (diff.actual ? 'Y' : 'N') : '-'}
                            </span>
                          </td>
                        );
                      }
                      const val = m.actual.service_criteria?.[k];
                      return (
                        <td key={k} className="py-1 pr-2 text-center">
                          {renderCriteriaBadge(val)}
                        </td>
                      );
                    })}
                    <td className="py-1">
                      {hasSignificantDiffs ? (
                        <span className="text-yellow-600 text-xs">changed</span>
                      ) : (
                        <span className="text-green-600 text-xs">match</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {renderMissingExtra(group.missing, 'red')}
      {renderMissingExtra(group.extra, 'yellow')}
    </div>
  );
};

const ComparisonDisplay: React.FC<{ comparison: GroundTruthComparison }> = ({ comparison }) => (
  <div className="border rounded p-3 bg-gray-50">
    <div className="flex items-center gap-2 mb-2">
      <VerdictBadge verdict={comparison.verdict} />
      <span className="text-xs text-gray-600">{comparison.summary}</span>
    </div>
    <MatchGroupTable group={comparison.interactions} label="Interactions" />
    <MatchGroupTable group={comparison.drink_promotions} label="Drink Promotions" isDrink />
  </div>
);

/**
 * Extracts per-metric status from a comparison for the Recent Runs table.
 * Returns an array of { key, status, tooltip } for each metric seen.
 */
function getMetricStatuses(
  comparison: GroundTruthComparison
): Array<{ key: string; status: 'match' | 'changed' | 'missing' | 'extra'; tooltip: string }> {
  const result: Array<{ key: string; status: 'match' | 'changed' | 'missing' | 'extra'; tooltip: string }> = [];

  // Collect all metric keys across the comparison
  const metricState: Record<string, { status: 'match' | 'changed' | 'missing' | 'extra'; details: string[] }> = {};

  const ensure = (k: string) => {
    if (!metricState[k]) metricState[k] = { status: 'match', details: [] };
  };

  // From matched interactions
  for (const m of comparison.interactions.matched) {
    const expCriteria = m.expected.service_criteria || {};
    const actCriteria = m.actual.service_criteria || {};
    const criteriaDiffs = m.diffs.service_criteria || {};
    const allKeys = Array.from(new Set([...Object.keys(expCriteria), ...Object.keys(actCriteria)]));
    for (const k of allKeys) {
      ensure(k);
      if (criteriaDiffs[k]) {
        metricState[k].status = 'changed';
        const exp = criteriaDiffs[k].expected;
        const act = criteriaDiffs[k].actual;
        metricState[k].details.push(
          `${m.type}: ${exp != null ? (exp ? 'Y' : 'N') : '-'} \u2192 ${act != null ? (act ? 'Y' : 'N') : '-'}`
        );
      }
    }
  }

  // From missing interactions — all their metrics are "missing"
  for (const m of comparison.interactions.missing) {
    for (const k of Object.keys(m.service_criteria || {})) {
      ensure(k);
      if (metricState[k].status !== 'changed') metricState[k].status = 'missing';
      metricState[k].details.push(`${m.type || '?'}: missing interaction`);
    }
  }

  // From extra interactions — their metrics are "extra"
  for (const m of comparison.interactions.extra) {
    for (const k of Object.keys(m.service_criteria || {})) {
      ensure(k);
      if (metricState[k].status === 'match') metricState[k].status = 'extra';
      metricState[k].details.push(`${m.type || '?'}: extra interaction`);
    }
  }

  // Also add a dot for drink_promotions if any exist
  const dp = comparison.drink_promotions;
  if (dp.matched.length > 0 || dp.missing.length > 0 || dp.extra.length > 0) {
    const dpDetails: string[] = [];
    let dpStatus: 'match' | 'changed' | 'missing' | 'extra' = 'match';
    if (dp.missing.length > 0) {
      dpStatus = 'missing';
      dpDetails.push(`${dp.missing.length} missing`);
    }
    if (dp.extra.length > 0) {
      if (dpStatus === 'match') dpStatus = 'extra';
      dpDetails.push(`${dp.extra.length} extra`);
    }
    if (dp.matched.some((m) => Object.entries(m.diffs).some(([, v]) => {
      if (v && typeof v === 'object' && 'within_tolerance' in v && (v as any).within_tolerance) return false;
      return true;
    }))) {
      if (dpStatus === 'match') dpStatus = 'changed';
      dpDetails.push('field changes');
    }
    if (dpDetails.length === 0) dpDetails.push('all matched');
    metricState['drinks'] = { status: dpStatus, details: dpDetails };
  }

  for (const [key, val] of Object.entries(metricState).sort(([a], [b]) => a.localeCompare(b))) {
    result.push({
      key,
      status: val.status,
      tooltip: `${key}: ${val.details.length > 0 ? val.details.join('; ') : 'matched'}`,
    });
  }

  return result;
}

const statusColor: Record<string, string> = {
  match: 'bg-green-500',
  changed: 'bg-yellow-400',
  missing: 'bg-red-500',
  extra: 'bg-orange-400',
};

const RunMetricDots: React.FC<{ comparison: GroundTruthComparison | null }> = ({ comparison }) => {
  if (!comparison) return <span className="text-gray-300 text-xs">-</span>;

  const metrics = getMetricStatuses(comparison);

  return (
    <div className="flex items-center gap-1">
      <VerdictBadge verdict={comparison.verdict} />
      {metrics.map((m) => (
        <span
          key={m.key}
          title={m.tooltip}
          className={`inline-block w-2.5 h-2.5 rounded-full cursor-default ${statusColor[m.status] || 'bg-gray-300'}`}
        />
      ))}
    </div>
  );
};

type GTInteraction = {
  interaction_type: string;
  start_timestamp: number;
  end_timestamp: number;
  service_criteria: Array<{ type: string; fulfilled: boolean }>;
  [key: string]: unknown;
};

type GTDrink = {
  start_timestamp: number;
  end_timestamp: number;
  drink_promoted: string;
  [key: string]: unknown;
};

const GroundTruthModal: React.FC<{
  expected: Record<string, unknown>;
  onClose: () => void;
  onSave: (updated: Record<string, unknown>) => Promise<void>;
}> = ({ expected, onClose, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [interactions, setInteractions] = useState<GTInteraction[]>(
    () => JSON.parse(JSON.stringify((expected.annotated_interactions as unknown[]) || []))
  );
  const [drinks, setDrinks] = useState<GTDrink[]>(
    () => JSON.parse(JSON.stringify((expected.drink_promotions as unknown[]) || []))
  );

  // Collect all metric keys across all interactions (union — handles varying prompts)
  const metricKeys = React.useMemo(() => {
    const keys = new Set<string>();
    for (const item of interactions) {
      for (const c of item.service_criteria || []) keys.add(c.type);
    }
    return Array.from(keys).sort();
  }, [interactions]);

  const updateField = (index: number, field: string, value: string) => {
    const updated = [...interactions];
    updated[index] = {
      ...updated[index],
      [field]: field === 'interaction_type' ? value : Number(value) || 0,
    };
    setInteractions(updated);
  };

  const toggleCriterion = (index: number, metricKey: string) => {
    const updated = [...interactions];
    const criteria = [...(updated[index].service_criteria || [])];
    const ci = criteria.findIndex((c) => c.type === metricKey);
    if (ci >= 0) {
      criteria[ci] = { ...criteria[ci], fulfilled: !criteria[ci].fulfilled };
    } else {
      criteria.push({ type: metricKey, fulfilled: true });
    }
    updated[index] = { ...updated[index], service_criteria: criteria };
    setInteractions(updated);
  };

  const removeInteraction = (index: number) => {
    setInteractions(interactions.filter((_, i) => i !== index));
  };

  const addInteraction = () => {
    setInteractions([
      ...interactions,
      {
        interaction_type: '',
        start_timestamp: 0,
        end_timestamp: 0,
        service_criteria: metricKeys.map((k) => ({ type: k, fulfilled: false })),
      },
    ]);
  };

  const addMetricKey = () => {
    const key = prompt('New metric key (e.g. metric_4):');
    if (!key || metricKeys.includes(key)) return;
    // Add this metric to all interactions
    setInteractions(
      interactions.map((item) => ({
        ...item,
        service_criteria: [...(item.service_criteria || []), { type: key, fulfilled: false }],
      }))
    );
  };

  const removeMetricKey = (metricKey: string) => {
    setInteractions(
      interactions.map((item) => ({
        ...item,
        service_criteria: (item.service_criteria || []).filter((c) => c.type !== metricKey),
      }))
    );
  };

  const updateDrinkField = (index: number, field: string, value: string) => {
    const updated = [...drinks];
    updated[index] = {
      ...updated[index],
      [field]: field === 'drink_promoted' ? value : Number(value) || 0,
    };
    setDrinks(updated);
  };

  const removeDrink = (index: number) => {
    setDrinks(drinks.filter((_, i) => i !== index));
  };

  const addDrink = () => {
    setDrinks([...drinks, { start_timestamp: 0, end_timestamp: 0, drink_promoted: '' }]);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = { ...expected, annotated_interactions: interactions, drink_promotions: drinks };
      await onSave(updated);
    } catch {
      alert('Failed to save ground truth');
    } finally {
      setSaving(false);
    }
  };

  const resetEdits = () => {
    setInteractions(JSON.parse(JSON.stringify((expected.annotated_interactions as unknown[]) || [])));
    setDrinks(JSON.parse(JSON.stringify((expected.drink_promotions as unknown[]) || [])));
    setEditing(false);
  };

  // Helpers for readonly view
  const getCriteriaMap = (criteria: Array<{ type: string; fulfilled: boolean }>) => {
    const map: Record<string, boolean> = {};
    for (const c of criteria || []) map[c.type] = c.fulfilled;
    return map;
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-semibold">Ground Truth</h3>
          <div className="flex items-center gap-2">
            {!editing && (
              <button
                onClick={() => setEditing(true)}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                Edit
              </button>
            )}
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">
              &times;
            </button>
          </div>
        </div>
        <div className="p-4 overflow-auto flex-1">
          {/* Interactions */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-1">
              <h4 className="text-sm font-medium text-gray-700">Interactions ({interactions.length})</h4>
              {editing && (
                <div className="flex gap-2">
                  <button onClick={addMetricKey} className="text-xs text-purple-600 hover:text-purple-800">+ Metric</button>
                  <button onClick={addInteraction} className="text-xs text-blue-600 hover:text-blue-800">+ Interaction</button>
                </div>
              )}
            </div>
            {interactions.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b text-left text-gray-500">
                      <th className="py-1 pr-1">Type</th>
                      <th className="py-1 pr-1">Start</th>
                      <th className="py-1 pr-1">End</th>
                      {metricKeys.map((k) => (
                        <th key={k} className="py-1 pr-1 text-center">
                          {k}
                          {editing && (
                            <button
                              onClick={() => removeMetricKey(k)}
                              className="ml-0.5 text-red-400 hover:text-red-600"
                              title={`Remove ${k}`}
                            >
                              &times;
                            </button>
                          )}
                        </th>
                      ))}
                      {editing && <th className="py-1 w-6"></th>}
                    </tr>
                  </thead>
                  <tbody>
                    {interactions.map((item, i) => {
                      const criteriaMap = getCriteriaMap(item.service_criteria);
                      return (
                        <tr key={i} className="border-b">
                          <td className="py-1 pr-1">
                            {editing ? (
                              <input
                                className="border rounded px-1 py-0.5 text-xs w-full min-w-[100px]"
                                value={item.interaction_type || ''}
                                onChange={(e) => updateField(i, 'interaction_type', e.target.value)}
                              />
                            ) : (
                              String(item.interaction_type || '-')
                            )}
                          </td>
                          <td className="py-1 pr-1">
                            {editing ? (
                              <input
                                type="number"
                                className="border rounded px-1 py-0.5 text-xs w-16"
                                value={item.start_timestamp}
                                onChange={(e) => updateField(i, 'start_timestamp', e.target.value)}
                              />
                            ) : (
                              String(item.start_timestamp ?? '-')
                            )}
                          </td>
                          <td className="py-1 pr-1">
                            {editing ? (
                              <input
                                type="number"
                                className="border rounded px-1 py-0.5 text-xs w-16"
                                value={item.end_timestamp}
                                onChange={(e) => updateField(i, 'end_timestamp', e.target.value)}
                              />
                            ) : (
                              String(item.end_timestamp ?? '-')
                            )}
                          </td>
                          {metricKeys.map((k) => (
                            <td key={k} className="py-1 pr-1 text-center">
                              {editing ? (
                                <button
                                  onClick={() => toggleCriterion(i, k)}
                                  className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                                    criteriaMap[k]
                                      ? 'bg-green-100 text-green-800'
                                      : 'bg-red-50 text-red-500'
                                  }`}
                                >
                                  {criteriaMap[k] ? 'Y' : 'N'}
                                </button>
                              ) : (
                                <span className={criteriaMap[k] ? 'text-green-600' : 'text-red-500'}>
                                  {criteriaMap[k] !== undefined ? (criteriaMap[k] ? 'Y' : 'N') : '-'}
                                </span>
                              )}
                            </td>
                          ))}
                          {editing && (
                            <td className="py-1">
                              <button
                                onClick={() => removeInteraction(i)}
                                className="text-red-400 hover:text-red-600 text-xs"
                              >
                                &times;
                              </button>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            {interactions.length === 0 && !editing && (
              <p className="text-gray-400 text-xs">None</p>
            )}
          </div>

          {/* Drink promotions */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-1">
              <h4 className="text-sm font-medium text-gray-700">Drink Promotions ({drinks.length})</h4>
              {editing && (
                <button onClick={addDrink} className="text-xs text-blue-600 hover:text-blue-800">+ Add</button>
              )}
            </div>
            {drinks.length > 0 && (
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b text-left text-gray-500">
                    <th className="py-1 pr-1">Start</th>
                    <th className="py-1 pr-1">End</th>
                    <th className="py-1 pr-1">Drink</th>
                    {editing && <th className="py-1 w-6"></th>}
                  </tr>
                </thead>
                <tbody>
                  {drinks.map((item, i) => (
                    <tr key={i} className="border-b">
                      <td className="py-1 pr-1">
                        {editing ? (
                          <input
                            type="number"
                            className="border rounded px-1 py-0.5 text-xs w-16"
                            value={item.start_timestamp}
                            onChange={(e) => updateDrinkField(i, 'start_timestamp', e.target.value)}
                          />
                        ) : (
                          String(item.start_timestamp ?? '-')
                        )}
                      </td>
                      <td className="py-1 pr-1">
                        {editing ? (
                          <input
                            type="number"
                            className="border rounded px-1 py-0.5 text-xs w-16"
                            value={item.end_timestamp}
                            onChange={(e) => updateDrinkField(i, 'end_timestamp', e.target.value)}
                          />
                        ) : (
                          String(item.end_timestamp ?? '-')
                        )}
                      </td>
                      <td className="py-1 pr-1">
                        {editing ? (
                          <input
                            className="border rounded px-1 py-0.5 text-xs w-full min-w-[100px]"
                            value={item.drink_promoted || ''}
                            onChange={(e) => updateDrinkField(i, 'drink_promoted', e.target.value)}
                          />
                        ) : (
                          String(item.drink_promoted || '-')
                        )}
                      </td>
                      {editing && (
                        <td className="py-1">
                          <button
                            onClick={() => removeDrink(i)}
                            className="text-red-400 hover:text-red-600 text-xs"
                          >
                            &times;
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {drinks.length === 0 && !editing && (
              <p className="text-gray-400 text-xs">None</p>
            )}
          </div>
        </div>
        <div className="p-4 border-t flex justify-between">
          {editing ? (
            <>
              <div className="flex gap-2">
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="px-4 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:opacity-50"
                >
                  {saving ? 'Saving...' : 'Save'}
                </button>
                <button
                  onClick={resetEdits}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded text-sm hover:bg-gray-200"
                >
                  Cancel
                </button>
              </div>
              <div />
            </>
          ) : (
            <>
              <div />
              <button
                onClick={onClose}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded text-sm hover:bg-gray-200"
              >
                Close
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================================
// Test Case Panel
// ============================================================

type TranscriptEntry = { speaker: string; text: string; timestamp: number };

const TestCasePanel: React.FC<{
  templateId: string;
  versions: PromptVersionSummary[];
  roleLabel: string;
}> = ({ templateId, versions, roleLabel }) => {
  const [testCases, setTestCases] = useState<TestCase[]>([]);
  const [testRuns, setTestRuns] = useState<TestRunSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [transcriptEntries, setTranscriptEntries] = useState<TranscriptEntry[]>([]);
  const [newSpeaker, setNewSpeaker] = useState(roleLabel);
  const [newText, setNewText] = useState('');
  const [runningTests, setRunningTests] = useState<Set<string>>(new Set());
  const [runAllInProgress, setRunAllInProgress] = useState(false);
  const [lastResult, setLastResult] = useState<TestRunResult | null>(null);
  const [runAllResults, setRunAllResults] = useState<Map<string, TestRunResult>>(new Map());
  const [viewingDetail, setViewingDetail] = useState<{ detail: TestRunDetail; testCaseName: string } | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [detailFullscreen, setDetailFullscreen] = useState(false);
  const [editingTestCase, setEditingTestCase] = useState<TestCase | null>(null);
  const [editName, setEditName] = useState('');
  const [editEntries, setEditEntries] = useState<TranscriptEntry[]>([]);
  const [editSpeaker, setEditSpeaker] = useState(roleLabel);
  const [editText, setEditText] = useState('');
  const [viewingGroundTruth, setViewingGroundTruth] = useState<{
    testCaseId: string;
    expected: Record<string, unknown>;
  } | null>(null);
  const textInputRef = useRef<HTMLInputElement>(null);
  const editTextInputRef = useRef<HTMLInputElement>(null);

  const activeVersion = versions.find((v) => v.is_active);
  const [selectedVersionId, setSelectedVersionId] = useState<string>(activeVersion?.id || '');

  // Keep selectedVersionId in sync when versions change (e.g. new version created)
  useEffect(() => {
    if (!selectedVersionId || !versions.find((v) => v.id === selectedVersionId)) {
      const active = versions.find((v) => v.is_active);
      if (active) setSelectedVersionId(active.id);
    }
  }, [versions, selectedVersionId]);

  const selectedVersion = versions.find((v) => v.id === selectedVersionId);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [cases, runs] = await Promise.all([
        promptApi.listTestCases(templateId),
        promptApi.listTestRuns(templateId),
      ]);
      setTestCases(cases);
      setTestRuns(runs);
    } catch (err) {
      console.error('Failed to load test data', err);
    } finally {
      setLoading(false);
    }
  }, [templateId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const addEntry = () => {
    const trimmed = newText.trim();
    if (!trimmed) return;
    const timestamp = transcriptEntries.length === 0 ? 10 : transcriptEntries[transcriptEntries.length - 1].timestamp + 1;
    setTranscriptEntries([...transcriptEntries, { speaker: newSpeaker, text: trimmed, timestamp }]);
    setNewText('');
    // Auto-alternate speaker
    setNewSpeaker(newSpeaker === 'Guest' ? roleLabel : 'Guest');
    setTimeout(() => textInputRef.current?.focus(), 0);
  };

  const removeEntry = (index: number) => {
    setTranscriptEntries(transcriptEntries.filter((_, i) => i !== index));
  };

  const handleCreate = async () => {
    if (!newName.trim()) {
      alert('Please enter a test case name.');
      return;
    }
    if (transcriptEntries.length === 0) {
      alert('Please add at least one transcript entry.');
      return;
    }
    try {
      await promptApi.createTestCase(templateId, {
        name: newName,
        transcript_json: transcriptEntries,
      });
      setShowCreate(false);
      setNewName('');
      setTranscriptEntries([]);
      setNewSpeaker(roleLabel);
      setNewText('');
      loadData();
    } catch (err) {
      alert('Save failed: ' + (err as Error).message);
    }
  };

  const handleDuplicate = async (tc: TestCase) => {
    try {
      await promptApi.createTestCase(templateId, {
        name: `${tc.name} (copy)`,
        transcript_json: tc.transcript_json,
        expected_interactions: tc.expected_interactions,
        source_url: tc.source_url,
      });
      loadData();
    } catch (err) {
      alert('Duplicate failed: ' + (err as Error).message);
    }
  };

  const handleRun = async (testCaseId: string, versionId: string) => {
    setRunningTests((prev) => new Set(prev).add(testCaseId));
    setLastResult(null);
    setRunAllResults(new Map());
    try {
      const result = await promptApi.runTest(testCaseId, versionId);
      setLastResult(result);
      loadData();
    } catch (err) {
      alert('Test run failed: ' + (err as Error).message);
    } finally {
      setRunningTests((prev) => {
        const next = new Set(prev);
        next.delete(testCaseId);
        return next;
      });
    }
  };

  const handleRunAll = async (versionId: string) => {
    if (testCases.length === 0) return;
    setRunAllInProgress(true);
    setLastResult(null);
    setRunAllResults(new Map());

    const allIds = testCases.map((tc) => tc.id);
    setRunningTests(new Set(allIds));

    const results = new Map<string, TestRunResult>();

    await Promise.allSettled(
      testCases.map(async (tc) => {
        try {
          const result = await promptApi.runTest(tc.id, versionId);
          results.set(tc.id, result);
          setRunAllResults((prev) => new Map(prev).set(tc.id, result));
        } catch (err) {
          results.set(tc.id, {
            id: '',
            status: 'error',
            result: null,
            comparison: null,
            duration_ms: 0,
            error_message: (err as Error).message,
          });
          setRunAllResults((prev) =>
            new Map(prev).set(tc.id, results.get(tc.id)!)
          );
        } finally {
          setRunningTests((prev) => {
            const next = new Set(prev);
            next.delete(tc.id);
            return next;
          });
        }
      })
    );

    setRunAllInProgress(false);
    loadData();
  };

  const handleClearGroundTruth = async (testCaseId: string) => {
    if (!window.confirm('Clear ground truth? The next run will auto-save a new one.')) return;
    try {
      await promptApi.clearGroundTruth(testCaseId);
      loadData();
    } catch (err) {
      alert('Failed to clear ground truth: ' + (err as Error).message);
    }
  };

  const handleSetGroundTruth = async (testCaseId: string, resultJson: Record<string, unknown>) => {
    try {
      await promptApi.setGroundTruth(testCaseId, resultJson);
      loadData();
    } catch (err) {
      alert('Failed to set ground truth: ' + (err as Error).message);
    }
  };

  const handleDelete = async (testCaseId: string) => {
    if (!window.confirm('Delete this test case?')) return;
    try {
      await promptApi.deleteTestCase(testCaseId);
      loadData();
    } catch (err) {
      alert('Delete failed: ' + (err as Error).message);
    }
  };

  const startEditing = (tc: TestCase) => {
    setEditingTestCase(tc);
    setEditName(tc.name);
    setEditEntries(tc.transcript_json ? [...tc.transcript_json] : []);
    setEditSpeaker(roleLabel);
    setEditText('');
    setShowCreate(false);
  };

  const cancelEditing = () => {
    setEditingTestCase(null);
    setEditName('');
    setEditEntries([]);
    setEditSpeaker(roleLabel);
    setEditText('');
  };

  const addEditEntry = () => {
    const trimmed = editText.trim();
    if (!trimmed) return;
    const timestamp = editEntries.length === 0 ? 10 : editEntries[editEntries.length - 1].timestamp + 1;
    setEditEntries([...editEntries, { speaker: editSpeaker, text: trimmed, timestamp }]);
    setEditText('');
    setEditSpeaker(editSpeaker === 'Guest' ? roleLabel : 'Guest');
    setTimeout(() => editTextInputRef.current?.focus(), 0);
  };

  const handleSaveEdit = async () => {
    if (!editingTestCase) return;
    if (!editName.trim()) {
      alert('Please enter a test case name.');
      return;
    }
    if (editEntries.length === 0) {
      alert('Please add at least one transcript entry.');
      return;
    }
    try {
      await promptApi.updateTestCase(editingTestCase.id, {
        name: editName,
        transcript_json: editEntries,
      });
      cancelEditing();
      loadData();
    } catch (err) {
      alert('Save failed: ' + (err as Error).message);
    }
  };

  const handleViewRunDetail = async (runId: string, testCaseName: string) => {
    setLoadingDetail(true);
    try {
      const detail = await promptApi.getTestRun(runId);
      setViewingDetail({ detail, testCaseName });
    } catch (err) {
      alert('Failed to load run details: ' + (err as Error).message);
    } finally {
      setLoadingDetail(false);
    }
  };

  if (loading) return <p className="text-sm text-gray-500">Loading test cases...</p>;

  return (
    <div className="mb-6">
      {/* Version selector */}
      <div className="flex items-center gap-3 mb-4 p-3 bg-gray-50 rounded-lg">
        <label className="text-sm font-medium text-gray-700 whitespace-nowrap">Test against:</label>
        <select
          className="border rounded px-2 py-1 text-sm flex-1 max-w-xs"
          value={selectedVersionId}
          onChange={(e) => setSelectedVersionId(e.target.value)}
        >
          {versions.map((v) => (
            <option key={v.id} value={v.id}>
              v{v.version_number}{v.is_active ? ' (active / production)' : ''}{v.change_notes ? ` — ${v.change_notes}` : ''}
            </option>
          ))}
        </select>
        {selectedVersion && !selectedVersion.is_active && (
          <span className="text-xs text-amber-600 font-medium">Testing against non-production version</span>
        )}
      </div>

      <div className="flex items-center justify-between mb-2">
        <h3 className="text-lg font-semibold">
          Test Cases
          {testCases.length > 0 && (
            <span className="ml-2 text-sm font-normal text-gray-500">
              ({testCases.length})
            </span>
          )}
        </h3>
        <div className="flex items-center gap-3">
          {selectedVersion && testCases.length > 0 && (
            <button
              onClick={() => handleRunAll(selectedVersion.id)}
              disabled={runAllInProgress || runningTests.size > 0}
              className="text-sm text-green-600 hover:text-green-800 disabled:opacity-50 font-medium"
            >
              {runAllInProgress
                ? `Running (${runningTests.size} remaining)...`
                : `Run All (v${selectedVersion.version_number})`}
            </button>
          )}
          <button
            onClick={() => setShowCreate(!showCreate)}
            className="text-sm text-blue-600 hover:text-blue-800"
          >
            {showCreate ? 'Cancel' : '+ New Test Case'}
          </button>
        </div>
      </div>

      {showCreate && (
        <div className="bg-gray-50 rounded p-3 mb-3">
          <input
            className="w-full border rounded px-2 py-1 text-sm mb-3"
            placeholder="Test case name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
          />

          <label className="block text-sm font-medium text-gray-700 mb-1">Transcript</label>

          {transcriptEntries.length > 0 && (
            <div className="border rounded bg-white mb-2 divide-y">
              {transcriptEntries.map((entry, i) => (
                <div key={i} className="flex items-center gap-2 px-3 py-1.5 text-sm">
                  <span className="font-medium text-gray-700 w-14 shrink-0">{entry.speaker}</span>
                  <input
                    type="number"
                    className="border rounded px-1 py-0.5 text-xs text-gray-500 w-14 shrink-0"
                    value={entry.timestamp}
                    onChange={(e) => {
                      const updated = [...transcriptEntries];
                      updated[i] = { ...entry, timestamp: Number(e.target.value) };
                      setTranscriptEntries(updated);
                    }}
                  />
                  <span className="flex-1 text-gray-800">{entry.text}</span>
                  <button
                    onClick={() => removeEntry(i)}
                    className="text-gray-400 hover:text-red-500 text-xs px-1"
                  >
                    &times;
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="flex gap-2 items-center mb-3">
            <select
              className="border rounded px-2 py-1 text-sm"
              value={newSpeaker}
              onChange={(e) => setNewSpeaker(e.target.value)}
            >
              <option value={roleLabel}>{roleLabel}</option>
              <option value="Guest">Guest</option>
            </select>
            <input
              ref={textInputRef}
              className="flex-1 border rounded px-2 py-1 text-sm"
              placeholder="Type what was said and press Enter..."
              value={newText}
              onChange={(e) => setNewText(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addEntry(); } }}
            />
            <button
              onClick={addEntry}
              className="px-3 py-1 bg-gray-200 text-gray-700 rounded text-sm hover:bg-gray-300"
            >
              Add
            </button>
          </div>

          <button
            onClick={handleCreate}
            className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
          >
            Create
          </button>
        </div>
      )}

      {testCases.length === 0 ? (
        <p className="text-gray-500 text-sm">No test cases yet.</p>
      ) : (
        <div className="space-y-1">
          {testCases.map((tc) =>
            editingTestCase?.id === tc.id ? (
              <div key={tc.id} className="border-2 border-blue-300 rounded p-3 bg-blue-50">
                <input
                  className="w-full border rounded px-2 py-1 text-sm mb-3"
                  placeholder="Test case name"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                />

                <label className="block text-sm font-medium text-gray-700 mb-1">Transcript</label>

                {editEntries.length > 0 && (
                  <div className="border rounded bg-white mb-2 divide-y">
                    {editEntries.map((entry, i) => (
                      <div key={i} className="flex items-center gap-2 px-3 py-1.5 text-sm">
                        <span className="font-medium text-gray-700 w-14 shrink-0">{entry.speaker}</span>
                        <input
                          type="number"
                          className="border rounded px-1 py-0.5 text-xs text-gray-500 w-14 shrink-0"
                          value={entry.timestamp}
                          onChange={(e) => {
                            const updated = [...editEntries];
                            updated[i] = { ...entry, timestamp: Number(e.target.value) };
                            setEditEntries(updated);
                          }}
                        />
                        <input
                          type="text"
                          className="flex-1 border rounded px-1 py-0.5 text-sm text-gray-800"
                          value={entry.text}
                          onChange={(e) => {
                            const updated = [...editEntries];
                            updated[i] = { ...entry, text: e.target.value };
                            setEditEntries(updated);
                          }}
                        />
                        <button
                          onClick={() => setEditEntries(editEntries.filter((_, j) => j !== i))}
                          className="text-gray-400 hover:text-red-500 text-xs px-1"
                        >
                          &times;
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex gap-2 items-center mb-3">
                  <select
                    className="border rounded px-2 py-1 text-sm"
                    value={editSpeaker}
                    onChange={(e) => setEditSpeaker(e.target.value)}
                  >
                    <option value={roleLabel}>{roleLabel}</option>
                    <option value="Guest">Guest</option>
                  </select>
                  <input
                    ref={editTextInputRef}
                    className="flex-1 border rounded px-2 py-1 text-sm"
                    placeholder="Type what was said and press Enter..."
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addEditEntry(); } }}
                  />
                  <button
                    onClick={addEditEntry}
                    className="px-3 py-1 bg-gray-200 text-gray-700 rounded text-sm hover:bg-gray-300"
                  >
                    Add
                  </button>
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={handleSaveEdit}
                    className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
                  >
                    Save
                  </button>
                  <button
                    onClick={cancelEditing}
                    className="px-3 py-1 bg-gray-100 text-gray-700 rounded text-sm hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div key={tc.id} className="border rounded px-3 py-1.5 text-sm">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2 min-w-0">
                    {(() => {
                      const lastRun = testRuns.find((r) => r.test_case_id === tc.id);
                      const verdict = lastRun?.comparison_json?.verdict;
                      const color = !lastRun ? 'bg-gray-300'
                        : lastRun.status !== 'completed' ? 'bg-red-500'
                        : verdict === 'pass' ? 'bg-green-500'
                        : verdict === 'warn' ? 'bg-yellow-400'
                        : verdict === 'fail' ? 'bg-red-500'
                        : 'bg-gray-300';
                      return <span className={`inline-block w-2.5 h-2.5 rounded-full shrink-0 ${color}`} title={lastRun ? `Last run: ${verdict || lastRun.status}` : 'Not run yet'} />;
                    })()}
                    <span className="font-medium truncate">{tc.name}</span>
                    <span className="text-gray-400 text-xs whitespace-nowrap">
                      · {tc.transcript_json?.length || 0} entries
                    </span>
                    {tc.source_url && (
                      <a
                        href={tc.source_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gray-400 hover:text-blue-600"
                        title="View source shift transcript"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </a>
                    )}
                    {runAllResults.has(tc.id) && (
                      <RunMetricDots comparison={runAllResults.get(tc.id)!.comparison} />
                    )}
                  </div>
                  <div className="flex items-center gap-3 text-sm whitespace-nowrap shrink-0">
                    {selectedVersion && (
                      <button
                        onClick={() => handleRun(tc.id, selectedVersion.id)}
                        disabled={runningTests.has(tc.id)}
                        className="text-green-600 hover:text-green-800 disabled:opacity-50 text-left"
                      >
                        {runningTests.has(tc.id) ? 'Running...' : `Run (v${selectedVersion.version_number})`}
                      </button>
                    )}
                    {tc.expected_interactions ? (
                      <button
                        onClick={() => setViewingGroundTruth({ testCaseId: tc.id, expected: tc.expected_interactions! })}
                        className="text-purple-600 hover:text-purple-800 w-14 text-left"
                      >
                        View GT
                      </button>
                    ) : (
                      <span className="w-14" />
                    )}
                    {tc.expected_interactions ? (
                      <button
                        onClick={() => handleClearGroundTruth(tc.id)}
                        className="text-orange-500 hover:text-orange-700 w-14 text-left"
                      >
                        Clear GT
                      </button>
                    ) : (
                      <span className="w-14" />
                    )}
                    <button
                      onClick={() => handleDuplicate(tc)}
                      className="text-green-600 hover:text-green-800"
                    >
                      Duplicate
                    </button>
                    <button
                      onClick={() => startEditing(tc)}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(tc.id)}
                      className="text-red-500 hover:text-red-700"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            )
          )}
        </div>
      )}

      {/* Last test result */}
      {lastResult && (
        <div className="mt-4 border rounded p-3 bg-blue-50">
          <h4 className="font-medium text-sm mb-2">
            Last Test Result ({lastResult.duration_ms}ms) -{' '}
            <span
              className={
                lastResult.status === 'completed' ? 'text-green-600' : 'text-red-600'
              }
            >
              {lastResult.status}
            </span>
          </h4>
          {lastResult.ground_truth_auto_saved && (
            <div className="mb-2 px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">
              First run — result saved as ground truth
            </div>
          )}
          {lastResult.error_message && (
            <p className="text-red-600 text-xs mb-2">{lastResult.error_message}</p>
          )}
          {lastResult.comparison && (
            <div className="mb-2">
              <ComparisonDisplay comparison={lastResult.comparison} />
            </div>
          )}
          {lastResult.result && (
            <details className="text-xs">
              <summary className="cursor-pointer text-gray-500 hover:text-gray-700 mb-1">
                Raw result JSON
              </summary>
              <pre className="bg-white rounded p-2 overflow-auto max-h-64">
                {JSON.stringify(lastResult.result, null, 2)}
              </pre>
            </details>
          )}
        </div>
      )}

      {/* Run All summary */}
      {runAllResults.size > 0 && !lastResult && (
        <div className="mt-4 border rounded p-3 bg-blue-50">
          <h4 className="font-medium text-sm mb-2">
            Run All Results ({runAllResults.size}/{testCases.length})
            {runAllInProgress && (
              <span className="ml-2 text-gray-500 font-normal">
                {runningTests.size} still running...
              </span>
            )}
          </h4>
          <table className="w-full text-xs">
            <tbody>
            {testCases.map((tc) => {
              const result = runAllResults.get(tc.id);
              if (!result) {
                if (runningTests.has(tc.id)) {
                  return (
                    <tr key={tc.id} className="text-gray-500">
                      <td className="py-0.5 pr-3 font-medium">{tc.name}</td>
                      <td className="py-0.5 pr-3" colSpan={3}>Running...</td>
                    </tr>
                  );
                }
                return null;
              }
              return (
                <tr key={tc.id}>
                  <td className="py-0.5 pr-3 font-medium text-gray-700">{tc.name}</td>
                  <td className="py-0.5 pr-3 text-gray-400 whitespace-nowrap">{result.duration_ms}ms</td>
                  <td className="py-0.5 pr-3 whitespace-nowrap">
                    {result.status === 'error' ? (
                      <span className="text-red-600">Error: {result.error_message}</span>
                    ) : result.comparison ? (
                      <VerdictBadge verdict={result.comparison.verdict} />
                    ) : null}
                  </td>
                  <td className="py-0.5 whitespace-nowrap">
                    {result.status !== 'error' && result.comparison && (
                      <div className="flex items-center gap-1">
                        {getMetricStatuses(result.comparison).map((m) => (
                          <span
                            key={m.key}
                            title={m.tooltip}
                            className={`inline-block w-2.5 h-2.5 rounded-full cursor-default ${statusColor[m.status] || 'bg-gray-300'}`}
                          />
                        ))}
                      </div>
                    )}
                    {result.ground_truth_auto_saved && (
                      <span className="text-blue-600 text-xs ml-2">(ground truth saved)</span>
                    )}
                  </td>
                </tr>
              );
            })}
            </tbody>
          </table>
        </div>
      )}

      {/* Test run history */}
      {testRuns.length > 0 && (
        <div className="mt-4">
          <h4 className="text-sm font-medium text-gray-700 mb-1">Recent Runs</h4>
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b text-left text-gray-500">
                <th className="py-1 pr-2">Test</th>
                <th className="py-1 pr-2">Version</th>
                <th className="py-1 pr-2">Status</th>
                <th className="py-1 pr-2">Results</th>
                <th className="py-1 pr-2">Duration</th>
                <th className="py-1 pr-2">Date</th>
                <th className="py-1"></th>
              </tr>
            </thead>
            <tbody>
              {testRuns.slice(0, 10).map((r) => (
                <tr key={r.id} className="border-b">
                  <td className="py-1 pr-2">{r.test_case_name}</td>
                  <td className="py-1 pr-2">v{r.version_number}</td>
                  <td className="py-1 pr-2">
                    <span
                      className={
                        r.status === 'completed' ? 'text-green-600' : 'text-red-600'
                      }
                    >
                      {r.status}
                    </span>
                  </td>
                  <td className="py-1 pr-2">
                    <RunMetricDots comparison={r.comparison_json} />
                  </td>
                  <td className="py-1 pr-2">{r.duration_ms ? `${r.duration_ms}ms` : '-'}</td>
                  <td className="py-1 pr-2 text-gray-500">
                    {r.created
                      ? new Date(r.created + 'Z').toLocaleString(undefined, {
                          month: 'numeric',
                          day: 'numeric',
                          year: '2-digit',
                          hour: 'numeric',
                          minute: '2-digit',
                        })
                      : '-'}
                  </td>
                  <td className="py-1">
                    <button
                      onClick={() => handleViewRunDetail(r.id, r.test_case_name)}
                      disabled={loadingDetail}
                      className="text-blue-600 hover:text-blue-800 disabled:opacity-50"
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Run detail modal */}
      {viewingDetail && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => { setViewingDetail(null); setDetailFullscreen(false); }}
        >
          <div
            className={`bg-white shadow-xl flex flex-col ${
              detailFullscreen
                ? 'fixed inset-0 rounded-none'
                : 'rounded-lg w-full max-w-3xl max-h-[85vh]'
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold">
                {viewingDetail.testCaseName}
                <span
                  className={`ml-2 text-sm font-normal ${
                    viewingDetail.detail.status === 'completed' ? 'text-green-600' : 'text-red-600'
                  }`}
                >
                  {viewingDetail.detail.status}
                </span>
              </h3>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setDetailFullscreen(!detailFullscreen)}
                  className="text-gray-400 hover:text-gray-600 text-sm px-1"
                  title={detailFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
                >
                  {detailFullscreen ? '⊡' : '⊞'}
                </button>
                <button
                  onClick={() => { setViewingDetail(null); setDetailFullscreen(false); }}
                  className="text-gray-400 hover:text-gray-600 text-xl leading-none"
                >
                  &times;
                </button>
              </div>
            </div>

            <div className="p-4 overflow-y-auto flex-1">
              <div className="flex gap-4 text-sm text-gray-600 mb-4">
                {viewingDetail.detail.duration_ms != null && (
                  <span>Duration: {viewingDetail.detail.duration_ms}ms</span>
                )}
                {viewingDetail.detail.input_tokens != null && (
                  <span>Input tokens: {viewingDetail.detail.input_tokens.toLocaleString()}</span>
                )}
                {viewingDetail.detail.output_tokens != null && (
                  <span>Output tokens: {viewingDetail.detail.output_tokens.toLocaleString()}</span>
                )}
                {viewingDetail.detail.created && (
                  <span>{new Date(viewingDetail.detail.created).toLocaleString()}</span>
                )}
              </div>

              {viewingDetail.detail.error_message && (
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-red-700 mb-1">Error</h4>
                  <pre className="text-xs bg-red-50 text-red-800 rounded p-3 whitespace-pre-wrap">
                    {viewingDetail.detail.error_message}
                  </pre>
                </div>
              )}

              {viewingDetail.detail.comparison_json && (
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-1">Comparison</h4>
                  {(() => {
                    const runAt = viewingDetail.detail.created;
                    const tcUpdated = viewingDetail.detail.test_case_updated_at;
                    if (!runAt || !tcUpdated) return null;
                    if (new Date(tcUpdated).getTime() <= new Date(runAt).getTime()) return null;
                    return (
                      <div className="mb-2 px-3 py-2 bg-amber-50 border border-amber-200 text-amber-800 text-xs rounded">
                        ⚠️ Ground truth was edited on {new Date(tcUpdated).toLocaleString()},
                        after this run completed at {new Date(runAt).toLocaleString()}.
                        The comparison below reflects the older ground truth — re-run the
                        test to see a diff against the current expected_interactions.
                      </div>
                    );
                  })()}
                  <ComparisonDisplay
                    comparison={viewingDetail.detail.comparison_json as unknown as GroundTruthComparison}
                  />
                </div>
              )}

              {viewingDetail.detail.result_json && (
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-1">Result</h4>
                  <pre className={`text-xs bg-gray-50 rounded p-3 overflow-auto whitespace-pre-wrap ${detailFullscreen ? '' : 'max-h-80'}`}>
                    {JSON.stringify(viewingDetail.detail.result_json, null, 2)}
                  </pre>
                </div>
              )}
            </div>

            <div className="p-4 border-t flex justify-between">
              {viewingDetail.detail.result_json ? (
                <button
                  onClick={async () => {
                    await handleSetGroundTruth(
                      viewingDetail.detail.test_case_id,
                      viewingDetail.detail.result_json!
                    );
                    setViewingDetail(null);
                    setDetailFullscreen(false);
                  }}
                  className="px-4 py-2 bg-purple-600 text-white rounded text-sm hover:bg-purple-700"
                >
                  Set as Ground Truth
                </button>
              ) : (
                <div />
              )}
              <button
                onClick={() => { setViewingDetail(null); setDetailFullscreen(false); }}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded text-sm hover:bg-gray-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View ground truth modal */}
      {viewingGroundTruth && (
        <GroundTruthModal
          expected={viewingGroundTruth.expected}
          onClose={() => setViewingGroundTruth(null)}
          onSave={async (updated) => {
            await handleSetGroundTruth(viewingGroundTruth.testCaseId, updated);
            setViewingGroundTruth(null);
          }}
        />
      )}
    </div>
  );
};

// ============================================================
// Game Mechanics Panel (read-only)
// ============================================================

// Parse a trailing level suffix like "(L1)", "[L2]", "(Level 3)" from an
// example string. Requires the literal "L" or "Level" so we don't strip
// stray trailing digits from real prose. Returns the stripped text and the
// detected level (defaults to 1 when no suffix is present).
function parseExampleLevel(raw: string): { text: string; level: 1 | 2 | 3 } {
  const match = raw.match(/\s*[([]\s*L(?:evel)?\s*([123])\s*[)\]]\s*$/i);
  if (!match) return { text: raw.trim(), level: 1 };
  const level = Number(match[1]) as 1 | 2 | 3;
  const text = raw.slice(0, match.index).replace(/[\s\-—–]+$/, '').trim();
  return { text, level };
}

function computeXp(base: number, level: 1 | 2 | 3, l2Bonus: number, l3Bonus: number): number {
  if (level === 2) return base + l2Bonus;
  if (level === 3) return base + l3Bonus;
  return base;
}

const DEFAULT_XP_CONFIG = {
  greeting_xp: 10,
  drink_promotion_xp: 15,
  l2_bonus: 2,
  l3_bonus: 4,
};
const DEFAULT_METRIC_XP = 15;

const LevelPill: React.FC<{ level: 1 | 2 | 3 }> = ({ level }) => {
  const color =
    level === 3
      ? 'bg-purple-100 text-purple-700'
      : level === 2
      ? 'bg-blue-100 text-blue-700'
      : 'bg-gray-100 text-gray-600';
  return (
    <span className={`inline-block text-xs font-medium px-1.5 py-0.5 rounded ${color}`}>
      L{level}
    </span>
  );
};

const XpCell: React.FC<{ xp: number }> = ({ xp }) => (
  <span className="font-semibold text-gray-900">
    {xp} <span className="text-xs font-normal text-gray-500">XP</span>
  </span>
);

const GameMechanicsPanel: React.FC<{
  versions: PromptVersionSummary[];
  activeVersionId: string | null;
}> = ({ versions, activeVersionId }) => {
  const defaultVersionId =
    activeVersionId
    || (versions.length > 0
      ? [...versions].sort((a, b) => b.version_number - a.version_number)[0].id
      : '');
  const [selectedVersionId, setSelectedVersionId] = useState<string>(defaultVersionId);
  const [version, setVersion] = useState<PromptTemplateVersion | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!selectedVersionId) {
      setVersion(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    promptApi
      .getVersion(selectedVersionId)
      .then((v) => {
        if (!cancelled) setVersion(v);
      })
      .catch((err) => {
        if (!cancelled) setError((err as Error).message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedVersionId]);

  if (versions.length === 0) {
    return <p className="text-gray-500">No versions available.</p>;
  }

  const sc: ServiceCriteria | undefined = version?.service_criteria;
  const xp = { ...DEFAULT_XP_CONFIG, ...(sc?.xp_config ?? {}) };
  const metricsWithExamples = (sc?.metrics ?? []).filter(
    (m) => (m.examples?.length ?? 0) > 0,
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-600">
          Read-only reference of which utterances earn which XP for this version. Use this to verify
          the app in the field.
        </p>
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-600">Version:</label>
          <select
            value={selectedVersionId}
            onChange={(e) => setSelectedVersionId(e.target.value)}
            className="text-sm border rounded px-2 py-1"
            disabled={loading}
          >
            {[...versions]
              .sort((a, b) => b.version_number - a.version_number)
              .map((v) => (
                <option key={v.id} value={v.id}>
                  v{v.version_number}
                  {v.id === activeVersionId ? ' (active)' : ''}
                </option>
              ))}
          </select>
        </div>
      </div>

      {loading && <p className="text-gray-500 text-sm">Loading…</p>}
      {error && !loading && (
        <p className="text-red-600 text-sm">Failed to load version: {error}</p>
      )}

      {version && !loading && (
        <>
          {/* Greeting */}
          <div className="bg-white border rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xl">👋</span>
              <h3 className="text-base font-semibold text-gray-900">Greeting</h3>
            </div>
            <p className="text-sm text-gray-500 mb-3">
              Any detected greeting at the start of an interaction.
            </p>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-gray-500 border-b">
                  <th className="py-2 pr-4">Example</th>
                  <th className="py-2 w-24">XP</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b last:border-0">
                  <td className="py-2 pr-4 text-gray-800">Any greeting detected</td>
                  <td className="py-2">
                    <XpCell xp={xp.greeting_xp} />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Metrics */}
          {metricsWithExamples.length === 0 && (
            <div className="bg-white border rounded-lg p-4 text-sm text-gray-500">
              No service-criteria metrics with examples configured for this version.
            </div>
          )}
          {metricsWithExamples.map((metric) => {
            const base = metric.xp_value ?? DEFAULT_METRIC_XP;
            return (
              <div key={metric.key} className="bg-white border rounded-lg p-4">
                <div className="flex items-center gap-2 mb-1">
                  {metric.emoji && <span className="text-xl">{parseEmojiString(metric.emoji)}</span>}
                  <h3 className="text-base font-semibold text-gray-900">{metric.label}</h3>
                </div>
                {metric.description && (
                  <p className="text-sm text-gray-500 mb-3">{metric.description}</p>
                )}
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wide text-gray-500 border-b">
                      <th className="py-2 pr-4">Example</th>
                      <th className="py-2 w-24">XP</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(metric.examples ?? []).map((raw, idx) => {
                      const { text, level } = parseExampleLevel(raw);
                      const hasExplicitLevel = /[([]\s*L(?:evel)?\s*[123]\s*[)\]]\s*$/i.test(raw);
                      const totalXp = computeXp(base, level, xp.l2_bonus, xp.l3_bonus);
                      return (
                        <tr key={idx} className="border-b last:border-0 align-top">
                          <td className="py-2 pr-4 text-gray-800">
                            <div className="flex items-start gap-2">
                              {hasExplicitLevel && <LevelPill level={level} />}
                              <span>{text}</span>
                            </div>
                          </td>
                          <td className="py-2">
                            <XpCell xp={totalXp} />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            );
          })}

          {/* Drink Promotion */}
          {sc?.drink_promotion?.enabled && (
            <div className="bg-white border rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xl">
                  {sc.drink_promotion.emoji ? parseEmojiString(sc.drink_promotion.emoji) : '🥤'}
                </span>
                <h3 className="text-base font-semibold text-gray-900">
                  {sc.drink_promotion.label || 'Drink Promotion'}
                </h3>
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-gray-500 border-b">
                    <th className="py-2 pr-4">Example</th>
                    <th className="py-2 w-24">XP</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b last:border-0">
                    <td className="py-2 pr-4 text-gray-800">
                      Promote{' '}
                      {sc.drink_promotion.drink_name
                        || sc.drink_promotion.label
                        || 'the featured drink'}
                    </td>
                    <td className="py-2">
                      <XpCell xp={xp.drink_promotion_xp} />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ============================================================
// Main PromptManagement Component
// ============================================================

const PromptManagement: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const tenantId = searchParams.get('tenantId') || '';

  const [templates, setTemplates] = useState<PromptTemplate[]>([]);
  const [selectedRole, setSelectedRole] = useState<string>('');
  const [templateDetail, setTemplateDetail] = useState<PromptTemplateDetail | null>(null);
  const [editingVersion, setEditingVersion] = useState<PromptTemplateVersion | null>(null);
  const [showEditor, setShowEditor] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'versions' | 'automated_validations' | 'testing' | 'game_mechanics'>('versions');

  // Tenant info
  const [tenantName, setTenantName] = useState('');
  const [tenantRoles, setTenantRoles] = useState<TenantRole[]>([]);

  // Prompt preview
  const [promptPreview, setPromptPreview] = useState<{ context: string; static_content: string } | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [promptCopied, setPromptCopied] = useState(false);

  // New template form
  const [showNewTemplate, setShowNewTemplate] = useState(false);
  const [newRole, setNewRole] = useState('');
  const [newName, setNewName] = useState('');
  const [copyFromVersionId, setCopyFromVersionId] = useState('');
  const [copySourceTemplates, setCopySourceTemplates] = useState<
    Array<{ tenantName: string; templates: PromptTemplate[] }>
  >([]);

  const loadTemplates = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    try {
      const data = await promptApi.listTemplates(tenantId);
      setTemplates(data);
      if (data.length > 0 && !selectedRole) {
        setSelectedRole(data[0].role);
      }
    } catch (err) {
      console.error('Failed to load templates', err);
    } finally {
      setLoading(false);
    }
  }, [tenantId, selectedRole]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  // Fetch tenant name and roles
  useEffect(() => {
    if (!tenantId) return;
    promptApi.getTenantName(tenantId).then(setTenantName).catch(() => setTenantName(tenantId));
    promptApi.listRoles(tenantId).then(setTenantRoles).catch(() => setTenantRoles([]));
  }, [tenantId]);

  // Load templates from all tenants when "New Template" form opens
  useEffect(() => {
    if (!showNewTemplate) return;
    let cancelled = false;
    (async () => {
      try {
        const tenants = await promptApi.listTenants();
        const groups: Array<{ tenantName: string; templates: PromptTemplate[] }> = [];
        for (const t of tenants) {
          try {
            const tpls = await promptApi.listTemplates(t.tenant_id);
            const withActiveVersion = tpls.filter((tp) => tp.active_version_id);
            if (withActiveVersion.length > 0) {
              groups.push({ tenantName: t.tenant_name, templates: withActiveVersion });
            }
          } catch {
            // Skip tenants we can't access
          }
        }
        if (!cancelled) setCopySourceTemplates(groups);
      } catch {
        if (!cancelled) setCopySourceTemplates([]);
      }
    })();
    return () => { cancelled = true; };
  }, [showNewTemplate]);

  // Load template detail when role changes, and re-fetch when the user
  // switches to the Versions tab — running tests in the Testing tab
  // updates the cached test_status on each version, but the Versions tab's
  // "Tests" badge is computed off templateDetail; without a refetch when
  // returning to Versions, the user can see stale "no test run" badges
  // immediately after a successful test run, and the activation
  // confirmation dialog will warn incorrectly.
  const selectedTemplate = templates.find((t) => t.role === selectedRole && !t.location_id);

  useEffect(() => {
    if (!selectedTemplate) {
      setTemplateDetail(null);
      return;
    }
    promptApi.getTemplate(selectedTemplate.id).then(setTemplateDetail).catch(console.error);
  }, [selectedTemplate?.id, activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleActivate = async (versionId: string) => {
    // Look up cached test status from the templateDetail we already have.
    const version = templateDetail?.versions.find((v) => v.id === versionId);
    const ts = version?.test_status;
    let prompt = 'Activate this version? It will be used for all new shifts.';
    if (ts?.state === 'failing') {
      const fails = (ts.fail ?? 0) + (ts.error ?? 0);
      prompt = `⚠️ This version has ${fails} failing test case${fails !== 1 ? 's' : ''} (last run ${ts.last_run ? new Date(ts.last_run).toLocaleString() : 'unknown'}). Activate anyway?`;
    } else if (ts?.state === 'never_run') {
      prompt = 'No test results recorded for this version. Activate anyway? (Run all tests first to see if it passes.)';
    } else if (ts?.state === 'warning') {
      prompt = `This version has ${ts.warn ?? 0} warning(s). Activate?`;
    }
    if (!window.confirm(prompt)) return;
    try {
      await promptApi.activateVersion(versionId);
      if (selectedTemplate) {
        const detail = await promptApi.getTemplate(selectedTemplate.id);
        setTemplateDetail(detail);
      }
    } catch (err) {
      alert('Failed to activate: ' + (err as Error).message);
    }
  };

  // The per-version "Run all tests" button was removed — testing happens
  // exclusively in the Testing tab via promptApi.runTest, which writes a
  // prompt_test_run row AND triggers the cache recompute. The Versions
  // tab's "Tests" badge stays accurate without a parallel run path.

  const handleViewVersion = async (versionId: string) => {
    try {
      const version = await promptApi.getVersion(versionId);
      setEditingVersion(version);
      setShowEditor(true);
    } catch (err) {
      alert('Failed to load version: ' + (err as Error).message);
    }
  };

  const handlePreviewPrompt = async (versionId: string) => {
    setLoadingPreview(true);
    try {
      const preview = await promptApi.previewAssembledPrompt(versionId);
      setPromptPreview(preview);
    } catch (err) {
      alert('Failed to load prompt preview: ' + (err as Error).message);
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleCreateTemplate = async () => {
    if (!newRole || !newName) {
      alert('Role and name are required');
      return;
    }
    try {
      const { id: newTemplateId } = await promptApi.createTemplate(tenantId, { role: newRole, name: newName });

      // Copy active version from source template if selected
      if (copyFromVersionId) {
        try {
          const sourceVersion = await promptApi.getVersion(copyFromVersionId);
          await promptApi.createVersion(newTemplateId, {
            context: sourceVersion.context,
            considerations: sourceVersion.considerations,
            interaction_types: sourceVersion.interaction_types,
            service_criteria: sourceVersion.service_criteria,
            menu: sourceVersion.menu,
            examples: sourceVersion.examples,
            output_format_template: sourceVersion.output_format_template,
            double_check_template: sourceVersion.double_check_template,
            judge_template: sourceVersion.judge_template,
            recommendations_template: sourceVersion.recommendations_template,
            analysis_requirements: sourceVersion.analysis_requirements,
            change_notes: `Copied from another template`,
          });
        } catch (copyErr) {
          console.error('Failed to copy version:', copyErr);
          alert('Template created but failed to copy version content: ' + (copyErr as Error).message);
        }
      }

      setShowNewTemplate(false);
      setNewRole('');
      setNewName('');
      setCopyFromVersionId('');
      await loadTemplates();
      setSelectedRole(newRole);
    } catch (err) {
      alert('Failed to create template: ' + (err as Error).message);
    }
  };

  const handleDeleteTemplate = async () => {
    if (!selectedTemplate) return;
    if (!window.confirm(`Delete template "${selectedTemplate.name}"? This cannot be undone.`)) return;
    try {
      await promptApi.deleteTemplate(selectedTemplate.id);
      setSelectedRole('');
      setTemplateDetail(null);
      loadTemplates();
    } catch (err) {
      alert('Failed to delete: ' + (err as Error).message);
    }
  };

  const roles = Array.from(new Set(templates.filter((t) => !t.location_id).map((t) => t.role)));

  // feat-319 deep link: `?tenantId=T&role=R&versionId=V` (from the shift
  // transcript header). Applied exactly ONCE, after the first template load
  // settles — closing the editor or switching roles never re-applies it.
  // Unknown role → ignored (today's default selection stands); unknown
  // version → the existing "Failed to load version" alert. `?tenantId=` alone
  // is a no-op here, so existing links behave exactly as before.
  const deepLinkRole = searchParams.get('role') || '';
  const deepLinkVersionId = searchParams.get('versionId') || '';
  const deepLinkAppliedRef = useRef(false);
  useEffect(() => {
    if (deepLinkAppliedRef.current) return;
    if (!tenantId || loading) return;
    deepLinkAppliedRef.current = true;
    if (deepLinkRole && roles.includes(deepLinkRole)) {
      setSelectedRole(deepLinkRole);
    }
    // UUID-shaped only: the id is interpolated into an API path.
    if (UUID_RE.test(deepLinkVersionId)) {
      handleViewVersion(deepLinkVersionId);
    }
  }, [tenantId, loading, templates]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!tenantId) {
    return (
      <div className="max-w-6xl mx-auto p-6">
        <p className="text-red-600">No tenant ID specified. Please navigate from the Tenant Dashboard.</p>
        <button
          onClick={() => navigate('/tenant_management')}
          className="mt-4 text-blue-600 hover:text-blue-800"
        >
          Go to Tenant Dashboard
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">
            Prompt Configuration{tenantName ? ` — ${tenantName}` : ''}
          </h1>
          <p className="text-sm text-gray-500">
            Manage AI prompt templates for interaction detection
          </p>
        </div>
        <button
          onClick={() => navigate('/tenant_management')}
          className="text-sm text-gray-600 hover:text-gray-800"
        >
          Back to Tenants
        </button>
      </div>

      {loading ? (
        <p className="text-gray-500">Loading...</p>
      ) : (
        <>
          <RoleSelector
            roles={roles}
            tenantRoles={tenantRoles}
            selected={selectedRole}
            onSelect={setSelectedRole}
            onCreate={() => setShowNewTemplate(true)}
          />

          {/* New template form */}
          {showNewTemplate && (
            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <h3 className="font-medium mb-2">Create New Template</h3>
              <div className="flex gap-2 items-end flex-wrap">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Role</label>
                  <select
                    className="border rounded px-2 py-1 text-sm"
                    value={newRole}
                    onChange={(e) => setNewRole(e.target.value)}
                  >
                    <option value="">Select a role...</option>
                    {tenantRoles
                      .filter((tr) => !roles.includes(tr.role_key))
                      .map((tr) => (
                        <option key={tr.id} value={tr.role_key}>
                          {tr.display_name}
                        </option>
                      ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Name</label>
                  <input
                    className="border rounded px-2 py-1 text-sm"
                    placeholder="e.g. Greeter Prompt"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Copy from (optional)</label>
                  <select
                    className="border rounded px-2 py-1 text-sm min-w-[220px]"
                    value={copyFromVersionId}
                    onChange={(e) => setCopyFromVersionId(e.target.value)}
                  >
                    <option value="">Start from scratch</option>
                    {copySourceTemplates.map((group) => (
                      <optgroup key={group.tenantName} label={group.tenantName}>
                        {group.templates.map((t) => (
                          <option key={t.id} value={t.active_version_id!}>
                            {t.name} ({t.role})
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                </div>
                <button
                  onClick={handleCreateTemplate}
                  className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700"
                >
                  Create
                </button>
                <button
                  onClick={() => { setShowNewTemplate(false); setCopyFromVersionId(''); }}
                  className="px-3 py-1 text-gray-600 text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Template detail */}
          {templateDetail && (
            <div className="bg-white border rounded-lg p-4">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-xl font-semibold">{templateDetail.name}</h2>
                  <p className="text-sm text-gray-500">
                    Role: {templateDetail.role} | Tenant default
                    {templateDetail.location_id ? ` | Location override` : ''}
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setEditingVersion(null);
                      setShowEditor(true);
                    }}
                    className="px-3 py-1.5 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
                  >
                    + New Version
                  </button>
                  <button
                    onClick={handleDeleteTemplate}
                    className="px-3 py-1.5 bg-red-50 text-red-600 rounded text-sm hover:bg-red-100 border border-red-200"
                  >
                    Delete Template
                  </button>
                </div>
              </div>

              {/* Tabs */}
              <div className="flex gap-1 border-b mb-4">
                <button
                  onClick={() => setActiveTab('versions')}
                  className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                    activeTab === 'versions'
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Versions
                </button>
                <button
                  onClick={() => setActiveTab('automated_validations')}
                  className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                    activeTab === 'automated_validations'
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Automated Validations
                </button>
                <button
                  onClick={() => setActiveTab('testing')}
                  className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                    activeTab === 'testing'
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Testing
                </button>
                <button
                  onClick={() => setActiveTab('game_mechanics')}
                  className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                    activeTab === 'game_mechanics'
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Game Mechanics
                </button>
              </div>

              {/* Versions + Automated Validations tabs share ONE VersionEditor
                  instance (same key + tree position) so the version form state is
                  preserved across the tab switch — a single save includes every
                  field regardless of which tab it was edited on. The `section`
                  prop selects which field group renders. */}
              {(activeTab === 'versions' || activeTab === 'automated_validations') && (
                <>
                  {/* Version editor (shared across both tabs) */}
                  {showEditor && (
                    <VersionEditor
                      key={editingVersion?.id ?? 'new'}
                      version={editingVersion}
                      section={activeTab === 'automated_validations' ? 'validations' : 'core'}
                      templateId={templateDetail.id}
                      isActive={editingVersion?.is_active ?? false}
                      onSaved={() => {
                        setShowEditor(false);
                        setEditingVersion(null);
                        promptApi.getTemplate(templateDetail.id).then(setTemplateDetail);
                      }}
                      onCancel={() => {
                        setShowEditor(false);
                        setEditingVersion(null);
                      }}
                      onPreview={handlePreviewPrompt}
                    />
                  )}

                  {/* Versions tab: the version list. */}
                  {activeTab === 'versions' && (
                    <VersionList
                      versions={templateDetail.versions}
                      activeVersionId={templateDetail.active_version_id}
                      onView={handleViewVersion}
                      onActivate={handleActivate}
                      onPreview={handlePreviewPrompt}
                    />
                  )}

                  {/* Automated Validations tab: empty-state when no version is open
                      for editing (the editor above renders once a version is opened). */}
                  {activeTab === 'automated_validations' && !showEditor && (
                    <div className="border border-dashed rounded-lg p-6 text-sm text-gray-500 bg-gray-50">
                      <p className="font-medium text-gray-700 mb-1">Double Check &amp; Judge prompts</p>
                      <p>
                        Open a version from the <span className="font-medium">Versions</span> tab (View or Edit)
                        to configure its Double Check and Judge prompts here. They save with the rest of the
                        version.
                      </p>
                    </div>
                  )}
                </>
              )}

              {/* Testing tab */}
              {activeTab === 'testing' && (
                <TestCasePanel
                  templateId={templateDetail.id}
                  versions={templateDetail.versions}
                  roleLabel={
                    tenantRoles.find((tr) => tr.role_key === templateDetail.role)?.display_name
                    || templateDetail.role
                  }
                />
              )}

              {/* Game Mechanics tab */}
              {activeTab === 'game_mechanics' && (
                <GameMechanicsPanel
                  versions={templateDetail.versions}
                  activeVersionId={templateDetail.active_version_id}
                />
              )}
            </div>
          )}

          {!templateDetail && selectedRole && (
            <p className="text-gray-500">
              No template found for role "{selectedRole}". Create one using the button above.
            </p>
          )}
        </>
      )}

      {/* Prompt preview modal */}
      {promptPreview && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setPromptPreview(null)}
        >
          <div
            className="bg-white fixed inset-4 rounded-lg shadow-xl flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold">Assembled Prompt</h3>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    const text = `System Message (context)\n\n${promptPreview.context}\n\nUser Message (static content)\n\n${promptPreview.static_content}`;
                    navigator.clipboard.writeText(text);
                    setPromptCopied(true);
                    setTimeout(() => setPromptCopied(false), 2000);
                  }}
                  className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                >
                  {promptCopied ? 'Copied!' : 'Copy'}
                </button>
                <button
                  onClick={() => setPromptPreview(null)}
                  className="text-gray-400 hover:text-gray-600 text-xl leading-none"
                >
                  &times;
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              <h4 className="text-sm font-medium text-gray-700 mb-1">System Message (context)</h4>
              <pre className="text-xs bg-gray-50 rounded p-3 whitespace-pre-wrap mb-4 font-mono">
                {promptPreview.context}
              </pre>
              <h4 className="text-sm font-medium text-gray-700 mb-1">User Message (static content)</h4>
              <pre className="text-xs bg-gray-50 rounded p-3 whitespace-pre-wrap font-mono">
                {promptPreview.static_content}
              </pre>
            </div>
            <div className="p-4 border-t flex justify-end">
              <button
                onClick={() => setPromptPreview(null)}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded text-sm hover:bg-gray-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {loadingPreview && (
        <div className="fixed inset-0 bg-black bg-opacity-25 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl px-6 py-4 text-sm text-gray-600">
            Loading prompt preview...
          </div>
        </div>
      )}
    </div>
  );
};

export default PromptManagement;
